import React, { useState, useEffect, useRef } from "react";
import { createJob, mergeJob, updateJob, findJobByNumber, findJobByTrackingNumber, baseJobNumber, logAudit, upsertLocationFromStop, getCompany, updateLocation, createLocation } from "../../services/firestore";
import { statusAdvanceFromSignatures } from "../../services/statusAdvance";
import { uploadGuardPhoto } from "../../services/storage";
import { todayYmd } from "../../services/billingReadiness";
import { resolveJobType, buildFerrariLocalStop } from "../../services/jobDirection";
import { ferrariParseWarnings, malcaParseWarnings } from "../../services/parseChecks";
import { cityForAirport } from "../../constants/airports";
import { useAuth } from "../../contexts/AuthContext";
import { collection, getDocs, query, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../firebase";
import PlacesAutocomplete from "../PlacesAutocomplete";
import { getPlacePredictions, getPlaceDetails } from "../../services/placesSearch";
import { findLocationMatch } from "../../services/locationIdentity";
import PhoneInput from "../PhoneInput";
import Time24Field from "../Time24Field";
import { guardOwnsCar } from "../../services/billing";
import SalesRepAutocomplete from "../SalesRepAutocomplete";
import { formatPhone } from "../../utils/phone";
import { useEscapeKey } from "../../hooks/useEscapeKey";
import { hawbLabel, hawbNumLabel } from "../../services/hawbLabel";

// ── Helpers ────────────────────────────────────────────────
function uid() { return Math.random().toString(36).slice(2, 9); }

// Normalize stop types based on jobType + position:
//   outbound:  pickup, pickup, ..., airport (last)
//   inbound:   airport (first), delivery, delivery, ...
//   roundtrip: airport (first), mixed deliveries/pickups, airport (last) —
//              each stop keeps its own type (the owner sets it per stop)
//   direct:    first stop = pickup (origin), last stop = delivery (destination),
//              any intermediates = pickup
function normalizeStopTypes(stops, jobType) {
  if (!Array.isArray(stops) || stops.length === 0) return stops;
  return stops.map((s, i, arr) => {
    if (jobType === "outbound") {
      return { ...s, type: s.type === "airport" ? "airport" : "pickup" };
    }
    if (jobType === "inbound") {
      return { ...s, type: s.type === "airport" ? "airport" : "delivery" };
    }
    if (jobType === "roundtrip") {
      if (s.type === "airport") return s;
      return { ...s, type: s.type === "pickup" ? "pickup" : "delivery" };
    }
    if (jobType === "direct") {
      // Single-stop direct shouldn't really happen, but call it pickup.
      if (arr.length === 1) return { ...s, type: "pickup" };
      // First = origin (pickup), last = destination (delivery), rest = pickup.
      if (i === 0) return { ...s, type: "pickup" };
      if (i === arr.length - 1) return { ...s, type: "delivery" };
      return { ...s, type: "pickup" };
    }
    return { ...s, type: s.type || "pickup" };
  });
}

// Fuzzy contact-dedup used by the parsed-contact preview. Mirrors
// mergeParsedContact() in firestore.js so the UI preview agrees with
// what the save path will do — single-character OCR drift on phone or
// last name reads as the same person.
export function isLikelySameContact(parsed, existing) {
  if (!parsed || !Array.isArray(existing) || existing.length === 0) return false;
  const normPhone = (p) => String(p || "").replace(/\D/g, "");
  function editDistanceOne(a, b) {
    if (a === b) return true;
    if (Math.abs(a.length - b.length) > 1) return false;
    let i = 0, j = 0, edits = 0;
    while (i < a.length && j < b.length) {
      if (a[i] === b[j]) { i++; j++; continue; }
      edits++;
      if (edits > 1) return false;
      if (a.length === b.length) { i++; j++; }
      else if (a.length > b.length) i++;
      else j++;
    }
    if (i < a.length || j < b.length) edits++;
    return edits <= 1;
  }
  function nameLooksSame(a, b) {
    const norm = (s) => String(s || "").toLowerCase().trim().replace(/\s+/g, " ");
    const aP = norm(a).split(" ").filter(Boolean);
    const bP = norm(b).split(" ").filter(Boolean);
    if (!aP.length || !bP.length) return false;
    if (aP[0] !== bP[0]) return false;
    const aLast = aP[aP.length - 1];
    const bLast = bP[bP.length - 1];
    if (aLast === aP[0] || bLast === bP[0]) return true;
    return aLast.slice(0, 3) === bLast.slice(0, 3);
  }
  const pDigits = normPhone(parsed.phone);
  return existing.some((c) => {
    const cDigits = normPhone(c.phone);
    if (pDigits && cDigits && editDistanceOne(pDigits, cDigits)) return true;
    if (parsed.name && c.name && nameLooksSame(parsed.name, c.name)) {
      // Same name but both sides carry clearly different numbers (not
      // OCR drift, caught above) — the save path appends that as a NEW
      // contact row, so the preview should say "new" too.
      if (pDigits && cDigits) return false;
      return true;
    }
    return false;
  });
}

// Read a File as base64 (strips the `data:...;base64,` prefix)
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload  = () => {
      const result = reader.result || "";
      const comma  = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Downscale an oversized pasted screenshot before it's shipped to the
// parser. The Anthropic API rejects any image over 10 MB, and it downscales
// anything larger than ~2576px on the long edge before the model reads it
// anyway — so re-encoding a huge paste to ≤2400px JPEG sheds bytes without
// losing reading accuracy. A full-screen 4K/Retina clipboard paste arrives
// as a >10 MB PNG, which is what caused the "image exceeds 10 MB maximum"
// parse failures. Images already small enough pass through untouched.
const IMAGE_MAX_EDGE = 2400;              // just under the API's ~2576px effective cap
const IMAGE_MAX_BYTES = 4 * 1024 * 1024;  // re-encode above this; base64 inflates ~4/3, so this stays far under 10 MB
export async function downscaleImage(file) {
  // Only raster images can go through a canvas — PDFs and anything else
  // pass through untouched (their size is guarded in addImageFiles).
  if (!String(file.type || "").startsWith("image/")) return file;
  // Small file → keep as-is. Pixel dimensions don't matter when the bytes
  // already fit; the API handles its own downscale for large-but-light images.
  if (file.size <= IMAGE_MAX_BYTES) return file;
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = reject;
      el.src = url;
    });
    const scale = Math.min(1, IMAGE_MAX_EDGE / Math.max(img.width, img.height));
    const canvas = document.createElement("canvas");
    canvas.width  = Math.max(1, Math.round(img.width * scale));
    canvas.height = Math.max(1, Math.round(img.height * scale));
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    // JPEG at 0.9 keeps screenshot text crisp; step down once if a
    // pathological image is still too heavy at 2400px.
    let blob = null;
    for (const quality of [0.9, 0.8]) {
      // eslint-disable-next-line no-await-in-loop
      blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
      if (blob && blob.size <= IMAGE_MAX_BYTES) break;
    }
    if (!blob) return file; // canvas encode failed — send the original and let the API decide
    const name = (file.name || "screenshot").replace(/\.[^.]+$/, "") + ".jpg";
    return new File([blob], name, { type: "image/jpeg" });
  } catch (err) {
    console.warn("Couldn't downscale pasted image; sending original", err);
    return file;
  } finally {
    URL.revokeObjectURL(url);
  }
}

// Note: billing clock start/end and flight lookup live at invoice time, not here.
// The fields remain in the form state (billingClockStart / billingClockEnd) so the
// data shape stays consistent — they're just left blank during job creation.

// Does this stop still refer to the given saved directory location? The
// parser can auto-link the WRONG location (stop.locationId); when the owner
// then fixes the stop's name/address, the stale link must not keep binding
// the stop to the old row — otherwise the contacts panel would show (and
// delete from!) contacts belonging to a location this stop no longer points
// at, and saving would overwrite the old row with the fixed values. A link
// still counts when the place is recognizably the same: matching Google
// place id, or name, or address — so a name-only fix keeps the link on
// purpose (renames are meant to update the saved row, not duplicate it).
function stopStillMatchesLocation(stop, loc) {
  if (!stop || !loc) return false;
  const norm = (s) => String(s || "").toLowerCase().replace(/\s+/g, " ").trim();
  if (stop.placeId && loc.placeId && stop.placeId === loc.placeId) return true;
  const sn = norm(stop.locationName);
  const ln = norm(loc.name);
  if (sn && ln && (sn.includes(ln) || ln.includes(sn))) return true;
  const sa = norm(stop.address);
  const la = norm(loc.address);
  if (sa && la && (sa.includes(la) || la.includes(sa))) return true;
  return false;
}

// A scheduled year far from today is almost always a typo (e.g. 2020 for
// 2026) — it silently files the job and its invoice under the wrong
// billing period (the "Ferrari Jun 2020" statement incident). Warn, don't
// block — genuinely far-off dates stay possible.
function scheduledYearLooksWrong(ymd) {
  const y = Number(String(ymd || "").slice(0, 4));
  if (!y) return false;
  const now = new Date().getFullYear();
  return y < now - 1 || y > now + 1;
}

const INITIAL_FORM = {
  jobNumber: "",
  jobType: "outbound",
  brokerId: "",
  brokerName: "",
  dispatcherName: "",
  // Sales rep on the job (the person the guard meets / texts the day before).
  // Critical for DAI which is paid per detail and bundled by rep across one
  // schedule email.
  salesRepName: "",
  salesRepPhone: "",
  // Bundle ID groups jobs created from the same dispatcher email (e.g. a DAI
  // schedule with 6 details → 6 jobs sharing one bundleId). Used by the
  // Invoices page to bundle them into a single submission.
  bundleId: "",
  airportCode: "CLT",
  scheduledDate: "",
  scheduledTime: "",
  // Broker-level OOT rule copied onto the job at creation. Drives the OOT
  // follow-time message on the guard side. Set whichever unit applies:
  //   ootFollowMinutes (DAI/LALI/EFFY) — guard follows for N minutes
  //   ootFollowMiles   (MWI)           — guard follows for N miles
  ootFollowMinutes: "",
  ootFollowMiles: "",
  // Whether the guard should text the sales rep the day before (Orion FAQ).
  // Mirrors broker.requiresRepConfirm so the My Jobs reminder card filters
  // on a per-job flag rather than guessing from the presence of a phone.
  requiresRepConfirm: false,
  isSplit: false,
  subJobLabel: "",
  parentJobNumber: "",
  billingClockStart: "",
  billingClockEnd: "",
  packages: [],
  stops: [],
  notes: "",
  // Internal-only notes — never displayed to guards. Visible to owners
  // on the job-detail screen and ready-to-bill review. Use case: pricing
  // reminders, billing quirks, broker-specific gotchas, "watch out for
  // this rep", etc. — anything you don't want the guard to see.
  ownerNotes: "",
  dispatcherEmailRaw: "",
  guardAssignments: [],
};

const GUARD_ROLES = {
  1: { title: "G1 — Lead car",         desc: "Time + mileage · photos" },
  2: { title: "G2 — Passenger with G1", desc: "Time only · no mileage" },
  3: { title: "G3 — Chase car",         desc: "Own clock · own mileage · no photos" },
  4: { title: "G4 — Passenger with G3", desc: "Time only · no mileage" },
};

// ── Claude API system prompt ───────────────────────────────
const PARSE_SYSTEM = `You are a logistics data parser for a high-value courier company. Extract job details from dispatcher emails. The input may be plain text, a screenshot image of the email (often a table), or both — use every source to produce the most accurate result. When reading a table image, preserve row associations: HAWB number, weight/value, consignee, address, and pickup time in the SAME ROW belong to the SAME package/stop. Return ONLY valid JSON with no explanation, no markdown, no backticks.

Extract these fields:
{
  "jobNumber": "string — e.g. MAKY26-1901",
  "jobType": "inbound or outbound or direct — INBOUND = packages arrive at airport to be delivered locally, OUTBOUND = packages picked up locally and brought to airport for departure, DIRECT = ground transport between two points no airport",
  "brokerHint": "broker name mentioned in email — e.g. Malca, EFFY, DAI, Orion, Ferrari",
  "airportCode": "3-letter airport code of the gateway — e.g. CLT, SDF",
  "scheduledDate": "YYYY-MM-DD if found",
  "scheduledTime": "HH:MM 24hr if found — overall job time",
  "guardsRequired": "integer — number of guards the dispatcher specified (e.g. '# OF GUARDS: 2' → 2). Default to 1 if not mentioned.",
  "packages": [
    {
      "hawbNumber": "string — the HAWB / airwaybill number",
      "description": "string — what's inside (jewelry, metal, etc.); 'Unknown' if not stated",
      "declaredValue": "number or null",
      "weight": "string or null — e.g. '25 lbs' or '120 lbs'",
      "pieces": "number or null — number of pieces in this package row",
      "stopIndex": "integer or null — which stop (by index in the stops array) this package belongs to"
    }
  ],
  "stops": [
    {
      "locationName": "string — consignee/business name",
      "address": "string — full street address",
      "type": "pickup or delivery or airport",
      "scheduledTime": "HH:MM 24hr or null — the pickup/delivery time for THIS stop"
    }
  ],
  "repName": "the SALES REP from the email header — the jewelry-store contact whose detail this is. NOT the dispatcher who sent the email. Look for labels like 'Sales rep', 'Contact', or a name+phone block at the top of the email associated with the jewelry company.",
  "repPhone": "the SALES REP phone (matches repName above), not the dispatcher's phone",
  "dispatcherName": "the Orion dispatcher who SENT the email — typically signed at the bottom (e.g. 'Bill Schmitt — Orion Security Services'). If only one name is present and you can't tell, leave this null and use repName.",
  "notes": "any other relevant details — MAWB #, special instructions, routing info",
  "details": [
    /*
      Populated whenever the email contains a SCHEDULE TABLE with 2+ FROM/TO
      rows under a single sales rep header. Common patterns:
        - DAI flat-rate schedules (6+ runs over the next month)
        - LALI / EFFY / Iberjoya / MWI hourly direct schedules (a couple of
          consecutive nights of escort work for one sales rep)
        - Any other broker that ships a multi-row schedule for one rep
      Each FROM/TO row = one entry. Leave this array empty for SINGLE-job
      emails (Malca outbound/inbound notifications, one-off direct requests).
    */
    {
      "scheduledDate": "YYYY-MM-DD",
      "scheduledTime": "HH:MM 24hr",
      "fromName": "origin store name (e.g. 'Kay Jewelers')",
      "fromAddress": "origin address — full street if available, else mall/center name",
      "toName": "destination store name OR 'OUT OF TOWN' if labeled OT/OOT/follow",
      "toAddress": "destination address or null if out of town",
      "outOfTown": "true if the destination is OT / OOT / 'OUT OF TOWN' / 'follow', else false"
    }
  ],
  "bulkJobs": [
    /*
      ONLY populated for Malca-style bulk schedule emails listing MANY
      INDEPENDENT outbound (or inbound) jobs in one message — each with its
      own job number. Layout is typically a table with columns:
      Job# | Date | Pickup Location(s) | Time | Airport | Direction.
      Each row → one entry here. A single row whose location cell has
      MULTIPLE store names stacked vertically = ONE job with MULTIPLE pickup
      stops (e.g. Apr 13: "Elemetal - Charlotte, NC" + "Diamonds Direct -
      Charlotte, NC" → one job MAKY26-1899 with 2 pickups).
      Leave this array empty for single-job emails or DAI-style flat-rate
      schedules (use "details" for those).
    */
    {
      "jobNumber": "e.g. MAKY26-1899",
      "jobType": "outbound or inbound",
      "scheduledDate": "YYYY-MM-DD",
      "scheduledTime": "HH:MM 24hr (parse '1530hrs', '15:30PM PICKUP', '15:30', etc.)",
      "airportCode": "3-letter airport code (e.g. CLT)",
      "stops": [
        {
          "locationName": "consignee/store name without city suffix",
          "address": "full street address if visible, else null",
          "type": "pickup or delivery"
        }
      ]
    }
  ]
}

IMPORTANT:
- For OUTBOUND jobs: each consignee row in the table is a PICKUP stop (we collect packages FROM them to bring to the airport)
- For INBOUND jobs: each consignee row is a DELIVERY stop (we deliver TO them from the airport)
- Set stopIndex to bind each package to its consignee row (0 = first stop, 1 = second stop, etc.)
- If a table cell reads "N @ Xlbs" (e.g. "2 @ 120lbs"), emit N SEPARATE package objects with the SAME stopIndex — one per physical package. Each gets weight: "120 lbs" and hawbNumber: "" if no HAWB is listed.
- If a package row has no HAWB (shows "0" or blank), still include it with hawbNumber: ""
- Every package MUST have a stopIndex — never leave it null if stops exist
- If a field cannot be determined, use null. Always return valid JSON.

SCHEDULE emails (multi-detail) — applies to ANY broker, not just DAI:
- These contain a TABLE of FROM/TO rows over multiple dates with one sales rep at the top.
- Always populate the "details" array with one entry per FROM/TO pair (each is a separate job).
- jobType for these is always "direct".
- "OT" or "OUT OF TOWN" or "follow" in either FROM or TO means outOfTown=true.
- A row pointing TO an airport (CLT Airport, "Hotel TBD" near an airport, "Airport — Hotel TBD")
  is NOT out-of-town — it's a real destination. Set outOfTown=false and put the airport/hotel
  text in toName.
- The fromAddress and toAddress columns may include full street addresses next to the names
  (e.g. "Kay Jewelers - GASTON MALL, NC | 407 Cox Rd., Gastonia, NC 28054"). Capture both:
  fromName="Kay Jewelers - GASTON MALL", fromAddress="407 Cox Rd., Gastonia, NC 28054".
- repName/repPhone come from the header of the schedule email, NOT from inside the table.
- Leave "stops" and "packages" empty for schedule emails — each detail becomes its own job
  with its own stops created from from/to.

BULK MALCA emails (multiple independent jobs):
- Recognized by a table where EACH ROW has its own job number (e.g. MAKY26-1899).
- Populate the "bulkJobs" array, one entry per row. Leave "details" empty.
- Each row has: a job number, a date, one or more pickup locations, a time, an airport code,
  and a direction (outbound/inbound). Time may appear in a header like "15:30PM PICKUP" applying
  to every row, or as "1530hrs" per row.
- IMPORTANT: when a single row's location cell has MULTIPLE store names stacked vertically
  (e.g. "Elemetal - Charlotte, NC" on one line and "Diamonds Direct - Charlotte, NC" on the next),
  that's ONE job with MULTIPLE pickup stops — emit one bulkJobs entry whose stops array
  contains both. Don't split into two jobs.
- Each location like "Elemetal - Charlotte, NC" should be parsed as locationName: "Elemetal"
  (drop the city suffix from the name; address can be the city if no street given).
- If the email has both a bulk Malca schedule AND single-job content, prefer "bulkJobs" — leave
  "stops"/"packages" empty.`;

// ══════════════════════════════════════════════════════════
// Main component
// ══════════════════════════════════════════════════════════
// initialRawEmail: email text to start with in the paste box (a Malca
// email-intake card that needs setting up by hand).
export default function JobCreateModal({ onClose, onCreated, companyId, initialRawEmail = "" }) {
  // Esc closes the modal, with a confirm if the user has typed/pasted anything
  // so a stray Escape key doesn't lose their work mid-create.
  useEscapeKey(() => {
    const dirty = rawEmail || pastedImages.length > 0 || form.brokerId || form.jobNumber || (form.stops || []).length > 0;
    if (dirty) {
      if (!window.confirm("Discard this new job?")) return;
    }
    onClose?.();
  });
  const { userProfile } = useAuth();
  const [step, setStep] = useState(1);
  const [rawEmail, setRawEmail] = useState(initialRawEmail);
  const [pastedImages, setPastedImages] = useState([]); // [{ id, file, name, preview, mediaType }]
  // Ferrari/Executive: keep the parsed attachment on the job as its package
  // document (POD) — the waybill fed to the parser IS the proof paperwork,
  // so saving it here means no re-upload at billing time. The sub-option
  // marks the job complete so a trip that already ran goes straight to
  // Ready to bill. Both default ON when a Ferrari waybill parse comes back.
  const [saveAttachmentAsDoc, setSaveAttachmentAsDoc] = useState(false);
  const [markCompleteOnSave, setMarkCompleteOnSave] = useState(false);
  const [parsing, setParsing] = useState(false);
  const imageInputRef = useRef();
  const [showParseWarning, setShowParseWarning] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [brokers, setBrokers] = useState([]);
  const [guards, setGuards] = useState([]);
  const [locations, setLocations] = useState([]);
  const [numGuards, setNumGuards] = useState(1);
  const [saving, setSaving] = useState(false);
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState("");
  // Broker-specific name for hawbNumber: Receipt No. (Malca) / Order No. (Ferrari)
  const pkgLabel = hawbLabel({ brokerName: form.brokerName });
  const pkgNumLabel = hawbNumLabel({ brokerName: form.brokerName });
  // Set after a single-job parse when a job with the same jobNumber already
  // exists for this company — drives the "already exists, merge into it?"
  // chip in step 2. Null when there's no collision (the common case).
  const [existingJobMatch, setExistingJobMatch] = useState(null);
  // Set when resolveJobType() couldn't confidently determine direction (the
  // cascade fell through to its fallback) — drives a "confirm direction"
  // banner in step 2. Null when direction resolved cleanly.
  const [directionReview, setDirectionReview] = useState(null);
  // Document sanity checks (Ferrari: missing/odd tracking #, package
  // number disagreeing with the form's tracking #; Malca: odd job
  // numbers, duplicate rows/receipts) — amber alert in step 2.
  const [parseWarnings, setParseWarnings] = useState([]);
  // Multi-detail flow (DAI-style schedule emails). When non-empty, step 2 shows
  // a checklist; on submit we loop createJob() once per checked detail with a
  // shared bundleId so they're invoiced together.
  const [parsedDetails, setParsedDetails] = useState([]);  // [{ scheduledDate, scheduledTime, fromName, fromAddress, toName, toAddress, outOfTown, _selected }]
  // Bulk Malca-style outbound/inbound jobs — separate from `parsedDetails`
  // because each entry is a complete job (own job#, own stops). When non-empty,
  // step 2 shows a per-row bulk preview and Create loops one createJob per row.
  const [parsedBulkJobs, setParsedBulkJobs] = useState([]);
  // Hub airport address/coords from Settings — used to auto-fill the airport
  // stop on outbound/inbound creates so the guard's tap-to-navigate goes to
  // your actual cargo terminal instead of the airport's passenger pin.
  const [companyDoc, setCompanyDoc] = useState(null);
  // Sales rep suggestions, derived from past jobs. We dedupe by
  // (brokerId, name-lowercased) so each broker has its own roster — DAI's
  // Amanda is a different person from Malca's Amanda even if they share a
  // first name. Each entry: { brokerId, name, phone, lastSeen }.
  const [knownReps, setKnownReps] = useState([]);
  // Load brokers, guards, locations, company once
  useEffect(() => {
    async function load() {
      const [bSnap, gSnap, lSnap, company, jSnap] = await Promise.all([
        getDocs(query(collection(db, "brokers"),   where("companyId", "==", companyId))),
        getDocs(query(collection(db, "users"),     where("companyId", "==", companyId), where("role", "in", ["guard", "owner"]))),
        getDocs(query(collection(db, "locations"), where("companyId", "==", companyId))),
        getCompany(companyId),
        // All jobs for the company. We only read salesRepName/phone/brokerId
        // off each one and discard the rest, so this isn't expensive even
        // at a few thousand jobs. Cached for the modal's lifetime.
        getDocs(query(collection(db, "jobs"), where("companyId", "==", companyId))),
      ]);
      setBrokers(bSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setGuards(gSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLocations(lSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setCompanyDoc(company);

      // Build the rep roster. Most-recent phone wins on duplicates so a
      // rep who changed numbers eventually surfaces the new one.
      const repMap = new Map();
      for (const d of jSnap.docs) {
        const j = d.data();
        const name = (j.salesRepName || "").trim();
        if (!name) continue;
        const key = `${j.brokerId || ""}::${name.toLowerCase()}`;
        const created = j.createdAt?.toDate?.()?.getTime?.() || 0;
        const cur = repMap.get(key);
        if (!cur || created > cur.lastSeen) {
          repMap.set(key, {
            brokerId: j.brokerId || "",
            name,
            phone: j.salesRepPhone || cur?.phone || "",
            lastSeen: created,
          });
        }
      }
      setKnownReps([...repMap.values()].sort((a, b) => b.lastSeen - a.lastSeen));
    }
    if (companyId) load();
  }, [companyId]);

  // Helper: build the airport-stop fields. Pulls cargo address + coords from
  // company settings if configured; falls back to the airport code only.
  // For inbound jobs, also pre-fills the scheduledTime with the company's
  // default flight arrival time (typically 05:15 for the SDF→CLT inbound).
  function buildAirportStopFields(jobType) {
    const inboundDefault = companyDoc?.inboundArrivalTime || "";
    return {
      type: "airport",
      locationName: companyDoc?.hubAirport || "CLT",
      address: companyDoc?.hubAirportAddress || "",
      lat: companyDoc?.hubAirportLat ?? null,
      lng: companyDoc?.hubAirportLng ?? null,
      accessNotes: companyDoc?.hubAirportNotes || "",
      scheduledTime: jobType === "inbound" ? inboundDefault : "",
    };
  }

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  // Whenever jobType, airportCode, or the stops array changes, (1) guarantee
  // an airport stop exists for inbound/outbound (auto-seeded in the correct
  // position), (2) keep the airport stop's locationName in sync with the job's
  // airportCode, and (3) re-normalize pickup/delivery types.
  useEffect(() => {
    setForm((f) => {
      let stops = [...f.stops];

      // Direct jobs have no airport — strip any airport stop that's still
      // there from a prior outbound/inbound selection.
      if (f.jobType === "direct") {
        stops = stops.filter((s) => s.type !== "airport");
      }

      // Round trip: airport bookends — collect inbound pieces at the start,
      // drop outbound pieces at the end. Seed whichever end is missing and
      // keep both pinned in position.
      if (f.jobType === "roundtrip") {
        const code = f.airportCode || companyDoc?.hubAirport || "CLT";
        stops = stops.map((s) =>
          s.type === "airport" && s.locationName !== code
            ? { ...s, locationName: code || s.locationName }
            : s
        );
        const airports = stops.filter((s) => s.type === "airport");
        const rest = stops.filter((s) => s.type !== "airport");
        const startAirport = airports[0] || {
          id: uid(),
          ...buildAirportStopFields("inbound"), // start leg gets the inbound arrival default
          locationName: code,
        };
        const endAirport = airports[1] || {
          id: uid(),
          ...buildAirportStopFields("outbound"),
          locationName: code,
        };
        stops = [startAirport, ...rest, endAirport];
      }

      if (f.jobType === "outbound" || f.jobType === "inbound") {
        // A prior roundtrip selection leaves two airport bookends — keep one.
        const firstAirportIdx = stops.findIndex((s) => s.type === "airport");
        stops = stops.filter((s, i) => s.type !== "airport" || i === firstAirportIdx);
        const hasAirport = stops.some((s) => s.type === "airport");
        if (!hasAirport) {
          const airportStop = {
            id: uid(),
            ...buildAirportStopFields(f.jobType),
            // Override the locationName from company hub with the form's
            // airportCode in case the user typed a different code in step 2.
            locationName: f.airportCode || companyDoc?.hubAirport || "CLT",
          };
          if (f.jobType === "outbound") stops.push(airportStop);
          else stops.unshift(airportStop);
        } else {
          // Keep airport stop's locationName aligned with the current airportCode
          stops = stops.map((s) =>
            s.type === "airport" && s.locationName !== f.airportCode
              ? { ...s, locationName: f.airportCode || s.locationName }
              : s
          );
          // Ensure airport is in the right position
          if (f.jobType === "outbound") {
            const airportIdx = stops.findIndex((s) => s.type === "airport");
            if (airportIdx !== stops.length - 1) {
              const [airport] = stops.splice(airportIdx, 1);
              stops.push(airport);
            }
          } else if (f.jobType === "inbound") {
            const airportIdx = stops.findIndex((s) => s.type === "airport");
            if (airportIdx !== 0) {
              const [airport] = stops.splice(airportIdx, 1);
              stops.unshift(airport);
            }
          }
        }
      }

      stops = normalizeStopTypes(stops, f.jobType);
      // Skip update if nothing changed (avoid render loop)
      const same = stops.length === f.stops.length &&
        stops.every((s, i) => s.id === f.stops[i].id && s.type === f.stops[i].type && s.locationName === f.stops[i].locationName);
      if (same) return f;
      return { ...f, stops };
    });
  }, [form.jobType, form.airportCode, form.stops.length, form.stops.map((s) => s.id).join(",")]);

  // ── Image paste / upload handlers ────────────────────────
  async function addImageFiles(files) {
    const all = Array.from(files || []);
    // Images and PDF attachments both parse; anything else gets a clear
    // message instead of silently vanishing.
    const accepted = all.filter((f) => f.type?.startsWith("image/") || f.type === "application/pdf");
    if (!accepted.length) {
      if (all.length) {
        setError(`Only images and PDFs can be parsed — "${all[0].name || "that file"}" is ${all[0].type || "a different type"}.`);
      }
      return;
    }
    // PDFs can't be downscaled like images, so cap their size up front —
    // keeps the parse request well under the backend's ~10 MB ceiling.
    const usable = [];
    for (const f of accepted) {
      if (f.type === "application/pdf" && f.size > 6 * 1024 * 1024) {
        setError(`"${f.name || "PDF"}" is too large to parse (${(f.size / 1024 / 1024).toFixed(1)} MB — max 6 MB). Screenshot the relevant pages instead.`);
        continue;
      }
      usable.push(f);
    }
    if (!usable.length) return;
    // Shrink oversized image pastes (4K/Retina screenshots) so the parse
    // request stays under the API's 10 MB per-image cap — see downscaleImage.
    const processed = await Promise.all(usable.map(downscaleImage));
    const additions = processed.map((file) => ({
      id: uid(),
      file,
      name: file.name || "",
      // PDFs have no <img> preview — rendered as a 📄 chip instead.
      preview: file.type === "application/pdf" ? null : URL.createObjectURL(file),
      mediaType: file.type || "image/png",
    }));
    setPastedImages((prev) => [...prev, ...additions]);
  }
  function handleImagePaste(e) {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (const it of items) {
      if (it.kind === "file") {
        const f = it.getAsFile();
        if (f && f.type?.startsWith("image/")) files.push(f);
      }
    }
    if (files.length) {
      e.preventDefault(); // don't dump binary junk into the textarea
      addImageFiles(files);
    }
  }

  // ── Drag & drop (Gmail attachments, files from Explorer) ──
  // Real file bytes arrive on drop when dragging from the OS (Explorer,
  // desktop, the browser's downloads bar) and from clients that expose
  // them. A Gmail attachment dragged straight across browser tabs
  // sometimes carries only a download LINK the app can't fetch (it would
  // need your Gmail login) — detect that and tell the owner the working
  // path instead of failing silently.
  const [dragOver, setDragOver] = useState(false);
  function dragHasPayload(e) {
    // Case-insensitive — Chrome reports "Files" but lowercases custom
    // types like DownloadURL (Gmail's attachment-drag payload).
    const types = Array.from(e.dataTransfer?.types || []).map((t) => String(t).toLowerCase());
    return types.includes("files") || types.includes("text/uri-list") || types.includes("downloadurl");
  }
  function handleDragOver(e) {
    if (!dragHasPayload(e)) return; // plain text drag → let the textarea take it natively
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    setDragOver(true);
  }
  function handleDragLeave(e) {
    // Ignore moves between children of the drop zone.
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setDragOver(false);
  }
  function handleDrop(e) {
    if (!dragHasPayload(e)) return;
    e.preventDefault();
    setDragOver(false);
    const dt = e.dataTransfer;
    const files = [];
    if (dt.files?.length) {
      files.push(...Array.from(dt.files));
    } else if (dt.items) {
      for (const it of dt.items) {
        if (it.kind === "file") {
          const f = it.getAsFile();
          if (f) files.push(f);
        }
      }
    }
    if (files.length) {
      setError("");
      addImageFiles(files);
      return;
    }
    // No bytes — the drag only carried a link (cross-tab Gmail drag).
    setError(
      "That drag only carried a link to the attachment, which this app can't download (it isn't signed into your Gmail). " +
      "Drag the attachment to your desktop or Downloads bar first, then drop the file here — or open the image and copy-paste it."
    );
  }
  function removeImage(id) {
    setPastedImages((prev) => {
      const gone = prev.find((p) => p.id === id);
      if (gone?.preview) URL.revokeObjectURL(gone.preview);
      return prev.filter((p) => p.id !== id);
    });
  }

  // ── Auto-verify parsed addresses ─────────────────────────
  // A parsed stop that didn't match a saved location lands with a raw
  // text address and no coords — the owner had to click into the box and
  // pick the Google suggestion to verify it (green ✓). This runs that
  // lookup automatically after parse. SAFETY: the top Google hit is only
  // accepted when it clearly IS the parsed address — same leading street
  // number and same ZIP (both anchors required when present, at least
  // one must match). Anything fuzzier stays unverified for a manual pick,
  // so a wrong silent auto-match can't slip into the job.
  function addressesLookSame(parsed, google) {
    const num = (s) => (String(s || "").trim().match(/^\d+/) || [])[0] || null;
    const zip = (s) => (String(s || "").match(/\b\d{5}\b(?!.*\b\d{5}\b)/) || [])[0] || null;
    const pNum = num(parsed), gNum = num(google);
    if (pNum && gNum && pNum !== gNum) return false;
    const pZip = zip(parsed), gZip = zip(google);
    if (pZip && gZip && pZip !== gZip) return false;
    // Require at least one hard anchor in common — a bare street name
    // match isn't enough to auto-accept.
    return !!((pNum && gNum) || (pZip && gZip));
  }
  async function verifyAddressViaPlaces(address) {
    try {
      const preds = await getPlacePredictions(address);
      if (!preds.length) return null;
      const details = await getPlaceDetails(preds[0].placeId);
      if (!details || details.lat == null) return null;
      if (!addressesLookSame(address, details.address)) return null;
      return details;
    } catch {
      return null;
    }
  }
  // Fire-and-forget over the single-job stops editor. Patches each stop
  // by id as results land, but only while the owner hasn't touched the
  // address and it's still unverified — a manual edit or Places pick in
  // the meantime wins. Keeps the stop's NAME (Google's name for a
  // residential address is just the address again).
  function autoVerifyParsedStops(stops) {
    (stops || []).forEach((s) => {
      if (s.type === "airport" || s.outOfTown) return;
      if (s.lat != null || s.locationId || !s.address) return;
      verifyAddressViaPlaces(s.address).then((details) => {
        if (!details) return;
        setForm((f) => ({
          ...f,
          stops: f.stops.map((cur) =>
            cur.id === s.id && cur.lat == null && cur.address === s.address
              ? { ...cur, address: details.address, lat: details.lat, lng: details.lng, placeId: details.placeId }
              : cur),
        }));
      });
    });
  }
  // Same treatment for bulk-Malca rows (keyed by row/stop index — bulk
  // stops have no ids). Bulk rows often carry only a city, which the
  // anchor check rejects, so those stay unverified as intended.
  function autoVerifyBulkStops(rows) {
    (rows || []).forEach((row, ri) => (row.stops || []).forEach((st, si) => {
      if (st.lat != null || st.locationId || !st.address) return;
      verifyAddressViaPlaces(st.address).then((details) => {
        if (!details) return;
        setParsedBulkJobs((arr) => arr.map((r, i) => (i !== ri ? r : {
          ...r,
          stops: r.stops.map((cur, j) =>
            j === si && cur.lat == null && cur.address === st.address
              ? { ...cur, address: details.address, lat: details.lat, lng: details.lng, placeId: details.placeId }
              : cur),
        })));
      });
    }));
  }

  // ── Step 1: Parse email ──────────────────────────────────
  async function handleParseEmail() {
    const hasText  = rawEmail.trim().length > 0;
    const hasImage = pastedImages.length > 0;
    if (!hasText && !hasImage) return;
    setParsing(true);
    setError("");
    setExistingJobMatch(null);
    setDirectionReview(null);
    setParseWarnings([]);
    try {
      // Pre-encode images to base64 so we can ship them through the
      // Cloud Function. The function expects [{ mediaType, data }] and
      // hands the request to Anthropic server-side — the API key never
      // touches the browser. System prompt + model selection are pinned
      // in functions/index.js so a malicious caller can't substitute
      // an expensive Opus prompt and run up the bill.
      const images = [];
      for (const img of pastedImages) {
        const b64 = await fileToBase64(img.file);
        images.push({ mediaType: img.mediaType, data: b64 });
      }

      const fn = httpsCallable(functions, "parseEmail");
      let data;
      try {
        // Pass the company's hub airport so the parser can do a
        // deterministic Ferrari direction check (DEST. AIRPORT vs hub).
        const result = await fn({
          rawText: hasText ? rawEmail : "",
          images,
          hubAirport: companyDoc?.hubAirport || "",
        });
        data = result.data;
      } catch (err) {
        // Callable errors arrive as { code, message } — surface message.
        throw new Error(err?.message || "Email parse failed");
      }
      // Log the full response so we can diagnose empty / malformed parses
      console.log("[JobCreateModal] Claude raw response:", data);
      console.log("[JobCreateModal] stop_reason:", data.stop_reason, "| usage:", data.usage);

      // Concatenate ALL text blocks (not just the first) in case the model splits output
      const textBlocks = (data.content || []).filter((b) => b.type === "text").map((b) => b.text).join("\n");
      console.log("[JobCreateModal] Concatenated text:", textBlocks);

      // Strip markdown code fences if the model wrapped the JSON
      let text = textBlocks.replace(/^```(?:json)?\s*/i, "").replace(/\s*```\s*$/i, "").trim();
      if (!text) {
        throw new Error("Model returned an empty response. Try parsing again or add text alongside the screenshot.");
      }
      // If the model wrapped JSON in prose, try to extract the outermost {...}
      if (!text.startsWith("{")) {
        const firstBrace = text.indexOf("{");
        const lastBrace  = text.lastIndexOf("}");
        if (firstBrace >= 0 && lastBrace > firstBrace) {
          text = text.slice(firstBrace, lastBrace + 1);
        }
      }
      let parsed;
      try {
        parsed = JSON.parse(text);
      } catch (jsonErr) {
        console.error("[JobCreateModal] JSON parse failed on:", text);
        throw new Error(`Model returned non-JSON (${jsonErr.message}). Raw text: ${text.slice(0, 200)}`);
      }
      console.log("[JobCreateModal] Parsed fields:", parsed);

      // Try to match broker from hint
      const matchedBroker = brokers.find((b) =>
        parsed.brokerHint && b.name.toLowerCase().includes(parsed.brokerHint.toLowerCase())
      );

      // ── Dedupe each parsed stop against the saved Locations directory ──
      // Without this, every parse produces "new" stops even when the
      // owner has visited the same store ten times before. We match by
      // (name, address) when both are present, then by name alone, then
      // by address alone — strongest signal wins. When matched, we copy
      // the saved address + coords (Places-verified, more reliable than
      // whatever the parser pulled out of the email) and stamp
      // locationId so the contacts-on-file panel surfaces.
      const norm = (s) => String(s || "").toLowerCase().replace(/\s+/g, " ").trim();
      // Stop↔location matching now uses the shared findLocationMatch()
      // (Place ID › name+address › coordinates › address › name) so the
      // job form, the background save, and the merge tool all agree.
      // Exact matches auto-link; uncertain ones are surfaced for the owner
      // to confirm (see the suggestedMatch handling in the stop mapping).

      // ── Direction is decided HERE, not by the AI ──────────────────
      // The parser returns raw signals in parsed.extracted; resolveJobType
      // applies the hub-relative cascade. parsed.jobType is only a soft
      // fallback for non-Ferrari emails (so Malca/Orion don't regress).
      const extracted = parsed.extracted || null;
      const shape = extracted?.documentShape || null;
      const isFerrari = shape === "ferrari_waybill" || shape === "ferrari_export_letter";
      const dir = resolveJobType(extracted || {}, companyDoc || {}, {
        statedDirection: extracted?.statedDirection,
        aiSuggestedJobType: parsed.jobType,
      });
      const resolvedJobType = dir.jobType;
      // Surface a "confirm direction" banner only when the cascade fell all
      // the way through to its fallback (couldn't tell).
      if (dir.needsReview) setDirectionReview(dir);

      // For Ferrari forms the AI no longer pre-builds my stop — it reports the
      // four raw address blocks and I pick the right one for MY leg here:
      // inbound → consignee (the local delivery); outbound → pickupInfo (the
      // local pickup). Non-Ferrari paths keep using parsed.stops as before.
      let sourceStops = parsed.stops || [];
      if (isFerrari) {
        const localStop = buildFerrariLocalStop(extracted, resolvedJobType);
        sourceStops = localStop ? [localStop] : [];
        // A Ferrari form with no usable local block is a parse miss — flag it.
        if (!localStop) setDirectionReview(dir.needsReview ? dir : { jobType: resolvedJobType, source: "ferrari_no_block", needsReview: true });
        // Form sanity checks — missing/odd tracking #, or a package
        // number disagreeing with the form's own tracking # (misread
        // crease). Advisory banner in step 2; never blocks. The no-stop
        // case is excluded here — the direction banner already covers it.
        setParseWarnings(ferrariParseWarnings({
          documentShape: extracted?.documentShape,
          trackingNumber: extracted?.trackingNumber,
          packages: parsed.packages || [],
          stop: localStop || { locationName: "n/a" },
        }));
      }

      // Build stops first, capture their ids, then resolve each package's stopIndex → stopId
      let newStops = sourceStops.map((s) => {
        const { match: m, confidence, reason } = findLocationMatch(locations, {
          name: s.locationName, address: s.address, lat: s.lat, lng: s.lng, placeId: s.placeId,
        });
        // EXACT match (Place ID, or identical name+address) → link
        // automatically; use the saved row as the source of truth for
        // address/coords, keeping the parser's name only if missing.
        if (m && confidence === "exact") {
          return {
            id: uid(),
            ...s,
            locationId: m.id,
            locationName: m.name || s.locationName || "",
            address: m.address || s.address || "",
            lat: typeof m.lat === "number" ? m.lat : s.lat ?? null,
            lng: typeof m.lng === "number" ? m.lng : s.lng ?? null,
            scheduledTime: s.scheduledTime || "",
          };
        }
        // UNCERTAIN match (same coordinates, same address, or same name) →
        // do NOT link silently. Attach a suggestion so the stop editor can
        // ask the owner to confirm (link) or keep it separate.
        if (m && (confidence === "strong" || confidence === "weak")) {
          return {
            id: uid(),
            ...s,
            scheduledTime: s.scheduledTime || "",
            suggestedMatch: { id: m.id, name: m.name || "", address: m.address || "", confidence, reason: reason || "" },
          };
        }
        return { id: uid(), ...s, scheduledTime: s.scheduledTime || "" };
      });

      // Auto-add airport stop if inbound/outbound and none present in parsed output.
      // ALWAYS use the courier's own hub airport — the form's "DEST. AIRPORT"
      // is Ferrari's final-mile destination, not where the courier drops the
      // package. The courier always operates from their hub (CLT for STSR).
      const hasAirport = newStops.some((s) => s.type === "airport");
      const hubAirport = companyDoc?.hubAirport || "";
      if (!hasAirport && ["inbound", "outbound"].includes(resolvedJobType)) {
        const airportStop = {
          id: uid(),
          ...buildAirportStopFields(resolvedJobType),
          locationName: hubAirport,
        };
        if (resolvedJobType === "inbound") {
          newStops = [airportStop, ...newStops]; // airport first
        } else {
          newStops = [...newStops, airportStop]; // airport last
        }
      }

      // Normalize types for every stop based on position + jobType
      newStops = normalizeStopTypes(newStops, resolvedJobType);

      // Force every airport stop to the courier's own hub, no matter what
      // the parser returned. The form's "DEST. AIRPORT" is Ferrari's
      // final-mile destination, not where the courier drives — those are
      // different airports for any outbound that isn't routed through the
      // courier's home airport.
      const hubCode = (companyDoc?.hubAirport || "").toUpperCase();
      if (hubCode) {
        const hubFields = buildAirportStopFields(resolvedJobType);
        newStops = newStops.map((s) => {
          if (s.type !== "airport") return s;
          return { ...s, ...hubFields, locationName: hubCode };
        });
      }
      // Post-process: expand "N @ Xlbs" packages (pieces > 1 with no HAWB) into N separate package rows
      const rawPackages = parsed.packages || [];
      const expandedPackages = [];
      for (const p of rawPackages) {
        const pieces = Number(p.pieces) || 1;
        const noHawb = !p.hawbNumber || String(p.hawbNumber).trim() === "";
        if (pieces > 1 && noHawb) {
          for (let i = 0; i < pieces; i++) {
            expandedPackages.push({ ...p, pieces: 1 });
          }
        } else {
          expandedPackages.push(p);
        }
      }
      // For inbound we prepended an airport stop — original index 0 is now index 1, etc.
      const stopIndexOffset = (resolvedJobType === "inbound" && !hasAirport) ? 1 : 0;
      // Ferrari forms have exactly one local (non-airport) stop; bind any
      // package the AI didn't index to it so nothing orphans.
      const ferrariLocalStopId = isFerrari
        ? (newStops.find((s) => s.type !== "airport") || {}).id || null
        : null;
      const newPackages = expandedPackages.map((p) => {
        const adjustedIdx = p.stopIndex != null ? p.stopIndex + stopIndexOffset : null;
        const resolvedStopId = (adjustedIdx != null && newStops[adjustedIdx])
          ? newStops[adjustedIdx].id
          : ferrariLocalStopId;
        return {
          id: uid(),
          hawbNumber:    p.hawbNumber    || "",
          description:   p.description   || "",
          declaredValue: p.declaredValue || "",
          weight:        p.weight        || "",
          pieces:        p.pieces        != null ? p.pieces : "",
          stopId:        resolvedStopId,
        };
      });

      // Apply parsed guard count (clamped to 1–4)
      const parsedGuards = Number(parsed.guardsRequired);
      let effectiveGuards = (parsedGuards >= 1 && parsedGuards <= 4) ? parsedGuards : null;

      // Ferrari rule: any single package over 100 lbs forces a 2-guard job
      // (two-person handling on heavy gold/metal). Belt-and-suspenders on top
      // of the parser — broker name comes from parsed.brokerHint or the
      // resolved broker match. Only bumps UP; never drops below what the
      // dispatcher already requested.
      const brokerLooksFerrari = String(parsed.brokerHint || matchedBroker?.name || "")
        .toLowerCase()
        .includes("ferrari");
      if (brokerLooksFerrari) {
        const heavy = (parsed.packages || []).some((p) => {
          const w = parseFloat(String(p.weight || "").replace(/[^0-9.]/g, ""));
          return Number.isFinite(w) && w > 100;
        });
        if (heavy) effectiveGuards = Math.max(2, effectiveGuards || 1);
      }

      if (effectiveGuards != null) setNumGuards(effectiveGuards);

      // Detect a bulk-Malca email (multiple independent jobs each with their
      // own job #). Different from DAI bundle: no shared bundleId, no shared
      // rep/broker logic — each row becomes a fully-independent job.
      const bulkJobs = Array.isArray(parsed.bulkJobs) ? parsed.bulkJobs : [];
      const isBulkMode = bulkJobs.length >= 2;
      if (isBulkMode) {
        // Same dedup logic as the single-job path — we want bulk parses
        // and the regular parser to produce identical "this is a match"
        // verdicts so the owner doesn't see different behavior across
        // surfaces. The findExistingLocation closure below relies on
        // the `norm` helper defined later in this function; we lift
        // both into module-level shape via a local re-declaration here.
        const norm = (s) => String(s || "").toLowerCase().replace(/\s+/g, " ").trim();
        const findMatch = (name, address) => {
          const nKey = norm(name);
          const aKey = norm(address);
          if (!nKey && !aKey) return null;
          if (nKey && aKey) {
            const both = locations.find((l) => norm(l.name) === nKey && norm(l.address) === aKey);
            if (both) return both;
          }
          if (nKey) {
            const byName = locations.filter((l) => norm(l.name) === nKey);
            if (byName.length === 1) return byName[0];
            if (byName.length > 1) {
              return byName.slice().sort((a, b) => {
                const at = a.lastUsedAt?.toMillis?.() || a.lastUsedAt || 0;
                const bt = b.lastUsedAt?.toMillis?.() || b.lastUsedAt || 0;
                return bt - at;
              })[0];
            }
          }
          if (aKey) {
            const byAddr = locations.find((l) => norm(l.address) === aKey);
            if (byAddr) return byAddr;
          }
          if (nKey) {
            const byCustomer = locations.filter((l) =>
              l.customerName && norm(l.customerName) === nKey
            );
            if (byCustomer.length === 1) return byCustomer[0];
          }
          return null;
        };
        const bulkRows = bulkJobs.map((bj) => ({
          ...bj,
          stops: (bj.stops || []).map((st) => {
            const m = findMatch(st.locationName, st.address);
            return {
              ...st,
              // Bind to the saved location when matched — that drives
              // the contacts-on-file panel + autocomplete in the form.
              locationId: m?.id || st.locationId || null,
              locationName: m?.name || st.locationName || "",
              address: m?.address || st.address || "",
              lat: typeof m?.lat === "number" ? m.lat : (st.lat ?? null),
              lng: typeof m?.lng === "number" ? m.lng : (st.lng ?? null),
            };
          }),
          _selected: true,
          _expanded: false,
          _guardCount: 1,   // guards on THIS row's job — editable per row below
        }));
        // Per-row dedup: flag any bulk row whose jobNumber already exists so
        // the save loop merges into it instead of creating a duplicate. The
        // row keeps the matched job's id + status for the inline badge.
        await Promise.all(bulkRows.map(async (row) => {
          const num = String(row.jobNumber || "").trim();
          if (!num) return;
          try {
            const match = await findJobByNumber(companyId, num);
            if (match) {
              row._existingJobId = match.id;
              row._existingJobStatus = match.status || "";
              row._existingJobNumber = match.jobNumber || "";
            }
          } catch (err) {
            console.warn("findJobByNumber failed for bulk row", num, err);
          }
        }));
        setParsedBulkJobs(bulkRows);
        autoVerifyBulkStops(bulkRows);
        // Malca bulk-table checks — missing/odd/duplicate job numbers,
        // dateless rows. Same advisory banner as the Ferrari checks.
        if (String(parsed.brokerHint || matchedBroker?.name || "").toLowerCase().includes("malca")) {
          setParseWarnings(malcaParseWarnings({ bulkRows }));
        }
      } else {
        setParsedBulkJobs([]);
        // Single-job Malca checks — odd job number, duplicate receipts.
        if (!isFerrari && String(parsed.brokerHint || matchedBroker?.name || "").toLowerCase().includes("malca")) {
          setParseWarnings(malcaParseWarnings({
            jobNumber: parsed.jobNumber,
            packages: parsed.packages || [],
          }));
        }
      }

      // Detect a multi-detail schedule email (DAI/bundled). When the parser
      // returns 2+ detail rows, switch into multi-detail mode; the broker/rep
      // are shared across all and each row becomes its own job on save.
      const details = Array.isArray(parsed.details) ? parsed.details : [];
      const multiDetail = !isBulkMode && details.length >= 2;
      // A single from→to detail (e.g. an EFFY direct) is NOT multi-detail — its
      // origin/destination get seeded straight into the stops editor below.
      // (The from/to row UI only renders for 2+, so otherwise they'd vanish.)
      const singleDetail = !isBulkMode && details.length === 1;

      // Dedup matcher for a parsed from/to against saved locations: exact
      // name+address > exact name (newest if ambiguous) > exact address >
      // customer-name. `norm` is already defined above in this handler.
      const findMatch = (name, address) => {
        const nKey = norm(name);
        const aKey = norm(address);
        if (!nKey && !aKey) return null;
        if (nKey && aKey) {
          const both = locations.find((l) => norm(l.name) === nKey && norm(l.address) === aKey);
          if (both) return both;
        }
        if (nKey) {
          const byName = locations.filter((l) => norm(l.name) === nKey);
          if (byName.length === 1) return byName[0];
          if (byName.length > 1) {
            return byName.slice().sort((a, b) => {
              const at = a.lastUsedAt?.toMillis?.() || a.lastUsedAt || 0;
              const bt = b.lastUsedAt?.toMillis?.() || b.lastUsedAt || 0;
              return bt - at;
            })[0];
          }
        }
        if (aKey) {
          const byAddr = locations.find((l) => norm(l.address) === aKey);
          if (byAddr) return byAddr;
        }
        if (nKey) {
          const byCustomer = locations.filter((l) =>
            l.customerName && norm(l.customerName) === nKey
          );
          if (byCustomer.length === 1) return byCustomer[0];
        }
        return null;
      };
      // Resolve a parsed detail's from/to against saved locations.
      const enrichDetail = (d) => {
        const fromMatch = findMatch(d.fromName, d.fromAddress);
        const toMatch = d.outOfTown ? null : findMatch(d.toName, d.toAddress);
        return {
          ...d,
          fromName: fromMatch?.name || d.fromName || "",
          fromAddress: fromMatch?.address || d.fromAddress || "",
          fromLat: typeof fromMatch?.lat === "number" ? fromMatch.lat : (d.fromLat ?? null),
          fromLng: typeof fromMatch?.lng === "number" ? fromMatch.lng : (d.fromLng ?? null),
          fromLocationId: fromMatch?.id || null,
          toName: d.outOfTown ? (d.toName || "OUT OF TOWN") : (toMatch?.name || d.toName || ""),
          toAddress: d.outOfTown ? "" : (toMatch?.address || d.toAddress || ""),
          toLat: d.outOfTown ? null : (typeof toMatch?.lat === "number" ? toMatch.lat : (d.toLat ?? null)),
          toLng: d.outOfTown ? null : (typeof toMatch?.lng === "number" ? toMatch.lng : (d.toLng ?? null)),
          toLocationId: d.outOfTown ? null : (toMatch?.id || null),
          notes: d.notes || "",
          _selected: true,
          _expanded: !d.outOfTown && !(fromMatch && toMatch), // open if anything still needs typing
        };
      };

      if (multiDetail) {
        setParsedDetails(details.map(enrichDetail));
      } else {
        setParsedDetails([]);
      }

      // Single direct detail → two editable stops (origin pickup, destination
      // delivery) so the parsed locations show in the stops editor and can be
      // corrected, instead of being silently dropped.
      let singleDetailStops = null;
      if (singleDetail) {
        const d = enrichDetail(details[0]);
        singleDetailStops = [
          {
            id: uid(), type: "pickup",
            locationId: d.fromLocationId || null,
            locationName: d.fromName || "",
            address: d.fromAddress || "",
            lat: typeof d.fromLat === "number" ? d.fromLat : null,
            lng: typeof d.fromLng === "number" ? d.fromLng : null,
            scheduledTime: "",
          },
          {
            id: uid(), type: "delivery",
            locationId: d.outOfTown ? null : (d.toLocationId || null),
            locationName: d.outOfTown ? "OUT OF TOWN" : (d.toName || ""),
            address: d.outOfTown ? "" : (d.toAddress || ""),
            lat: d.outOfTown ? null : (typeof d.toLat === "number" ? d.toLat : null),
            lng: d.outOfTown ? null : (typeof d.toLng === "number" ? d.toLng : null),
            scheduledTime: "",
            outOfTown: !!d.outOfTown,
          },
        ];
      }

      setForm((f) => ({
        ...f,
        dispatcherEmailRaw: rawEmail,
        // Ferrari paper forms don't carry a broker job number — the AI puts
        // the "Order / Tracking #" (e.g. NYW600155553) in extracted.trackingNumber
        // instead. Use it as the job number so the inbox shows the order #.
        jobNumber:    parsed.jobNumber || (isFerrari ? extracted?.trackingNumber : null) || f.jobNumber,
        jobType:      (multiDetail || singleDetail) ? "direct" : resolvedJobType,
        // Airport always defaults to the courier's hub, regardless of
        // the form's DEST. AIRPORT (that's Ferrari's final destination).
        airportCode:  companyDoc?.hubAirport || f.airportCode,
        scheduledDate: parsed.scheduledDate || f.scheduledDate,
        scheduledTime: parsed.scheduledTime || (singleDetail ? (details[0]?.scheduledTime || "") : "") || f.scheduledTime,
        brokerId:     matchedBroker?.id   || f.brokerId,
        brokerName:   matchedBroker?.name || parsed.brokerHint || f.brokerName,
        // Dispatcher comes from the matched broker's RATE CARD — the card is
        // the source of truth for who dispatches a broker (e.g. Kallati →
        // Executive Security Services, even when the email itself reads like
        // an Orion send). The parser's dispatcherName is only a fallback for
        // unmatched brokers; never use repName here (rep ≠ dispatcher).
        dispatcherName: matchedBroker?.dispatcherName
          || matchedBroker?.defaultDispatcher
          || parsed.dispatcherName
          || f.dispatcherName,
        // Broker-derived flags — mirror handleBrokerChange. Without these,
        // AI-parsed jobs never inherited requiresRepConfirm or the OOT
        // follow rule unless the owner manually re-picked the broker in
        // the dropdown, so day-before rep-confirm reminders (dashboard
        // tile + guard card) silently skipped parsed jobs.
        ootFollowMinutes: matchedBroker?.ootFollowMinutes || f.ootFollowMinutes || "",
        ootFollowMiles:   matchedBroker?.ootFollowMiles   || f.ootFollowMiles   || "",
        requiresRepConfirm: matchedBroker
          ? (matchedBroker.requiresRepConfirm ?? false)
          : f.requiresRepConfirm,
        // Sales rep = the jewelry-store contact whose detail this is.
        salesRepName:   parsed.repName    || f.salesRepName,
        salesRepPhone:  parsed.repPhone ? formatPhone(parsed.repPhone) : f.salesRepPhone,
        stops:    multiDetail ? [] : (singleDetail ? singleDetailStops : newStops),
        packages: multiDetail ? [] : (singleDetail ? [] : newPackages),
        notes:    parsed.notes || f.notes,
      }));

      // Kick off Places verification for any parsed stop that didn't
      // match a saved location — the green ✓ appears as each address
      // confirms, no clicking into the box needed.
      if (!multiDetail) {
        autoVerifyParsedStops(singleDetail ? singleDetailStops : newStops);
      }

      // Ferrari waybill parses: default "save attachment as package document"
      // ON — the fed-in waybill IS the POD paperwork for these jobs. Also
      // default "mark complete" when the trip date is today-or-earlier (the
      // owner usually gets the signed form AFTER the run), so the job lands
      // straight in Ready to bill. Only image attachments qualify — the POD
      // panel and POD PDF render images, not PDFs.
      const hasImageAttachment = pastedImages.some((p) => (p.mediaType || "").startsWith("image/"));
      setSaveAttachmentAsDoc(isFerrari && hasImageAttachment);
      setMarkCompleteOnSave(
        isFerrari && hasImageAttachment
        && !!parsed.scheduledDate && parsed.scheduledDate <= todayYmd()
      );

      // Single-job dedup: if a job with this number already exists, surface
      // a "merge into it?" chip in step 2 rather than blindly creating a
      // duplicate. Only for the single-job path — bulk rows are flagged
      // per-row above, and DAI-style multi-detail emails don't carry a
      // shared job number. Ferrari forms often have a blank jobNumber, so
      // they match on the tracking number instead (jobNumber first, then
      // packages[].hawbNumber) — that's the re-upload case: a SIGNED copy
      // of an already-created job comes back and should ADVANCE the job,
      // not duplicate it. The parse signatures ride along on the match
      // (underscore field, never persisted) to drive the advance chip.
      const dedupNumber = parsed.jobNumber || (isFerrari ? extracted?.trackingNumber : null);
      if (!isBulkMode && !multiDetail && dedupNumber) {
        try {
          const match = isFerrari
            ? await findJobByTrackingNumber(companyId, dedupNumber)
            : await findJobByNumber(companyId, dedupNumber);
          setExistingJobMatch(match
            ? { ...match, _sigs: isFerrari ? (extracted?.signaturesPresent || null) : null }
            : null);
        } catch (err) {
          console.warn("existing-job lookup failed", err);
        }
      }

      setShowParseWarning(true);
      setStep(2);
    } catch (e) {
      console.error("Parse error:", e);
      const msg = e?.message || String(e);
      // Two+ attached images that fail to parse are usually SEPARATE forms
      // (e.g. two Ferrari PODs) crammed into one call — the model emits one
      // JSON object per form and the parse chokes after the first. Point
      // the owner at the bulk flow, which runs one call per image.
      const bulkHint = pastedImages.length >= 2
        ? " If these are separate Ferrari POD forms, use the ➕ Bulk-upload Ferrari PODs button on the Job inbox instead — it parses each image on its own."
        : "";
      setError(`Parsing failed (${msg}) — fields left blank for manual entry.${bulkHint}`);
      set("dispatcherEmailRaw", rawEmail);
      setStep(2);
    }
    setParsing(false);
  }

  // ── Broker selection ─────────────────────────────────────
  function handleBrokerChange(brokerId) {
    const broker = brokers.find((b) => b.id === brokerId);
    setForm((f) => ({
      ...f,
      brokerId,
      brokerName:     broker?.name || "",
      dispatcherName: broker?.dispatcherName || broker?.defaultDispatcher || f.dispatcherName,
      // Copy the broker's OOT follow rule onto the job so the guard sees the
      // right duration/distance without fetching the broker doc on the phone.
      ootFollowMinutes: broker?.ootFollowMinutes || f.ootFollowMinutes || "",
      ootFollowMiles:   broker?.ootFollowMiles   || f.ootFollowMiles   || "",
      requiresRepConfirm: broker?.requiresRepConfirm ?? f.requiresRepConfirm ?? false,
    }));
  }

  // ── Packages ─────────────────────────────────────────────
  function addPackage(stopId = null) {
    set("packages", [...form.packages, {
      id: uid(), hawbNumber: "", description: "", declaredValue: "",
      weight: "", pieces: "", stopId,
    }]);
  }
  function removePackage(id) {
    set("packages", form.packages.filter((p) => p.id !== id));
  }
  function updatePackage(id, k, v) {
    // Same functional-setForm pattern as updateStop — protect against
    // the stale-closure bug if multiple package fields ever get updated
    // in quick succession.
    setForm((prev) => ({
      ...prev,
      packages: prev.packages.map((p) => p.id === id ? { ...p, [k]: v } : p),
    }));
  }
  function reassignPackage(id, stopId) {
    set("packages", form.packages.map((p) => p.id === id ? { ...p, stopId: stopId || null } : p));
  }

  // ── Stops ─────────────────────────────────────────────────
  function addStop() {
    const newStop = {
      id: uid(),
      locationName: "",
      address: "",
      lat: null,
      lng: null,
      type: "pickup", // normalizer will fix it
      scheduledTime: "",
    };

    setForm((f) => {
      const stops = [...f.stops];

      // Outbound: airport is always LAST → insert new pickup before airport
      if (f.jobType === "outbound") {
        const lastIdx = stops.length - 1;
        const hasAirportLast = stops[lastIdx]?.type === "airport";
        if (hasAirportLast) {
          stops.splice(lastIdx, 0, newStop);
        } else {
          // No airport yet — add this stop, then also ensure an airport stop exists at the end
          stops.push(newStop);
          if (!stops.some((s) => s.type === "airport")) {
            stops.push({
              id: uid(),
              ...buildAirportStopFields(f.jobType),
              locationName: f.airportCode || companyDoc?.hubAirport || "CLT",
            });
          }
        }
      }
      // Roundtrip: airport bookends — insert before the trailing airport.
      // Default the new stop to delivery; the per-stop type picker flips it.
      else if (f.jobType === "roundtrip") {
        newStop.type = "delivery";
        const lastIdx = stops.length - 1;
        if (stops[lastIdx]?.type === "airport") stops.splice(lastIdx, 0, newStop);
        else stops.push(newStop); // seeding effect will add the bookends
      }
      // Inbound: airport is always FIRST → auto-seed airport if missing, then append deliveries
      else if (f.jobType === "inbound") {
        if (stops.length === 0) {
          stops.push({
            id: uid(),
            ...buildAirportStopFields(f.jobType),
            locationName: f.airportCode || companyDoc?.hubAirport || "CLT",
          });
        }
        stops.push(newStop);
      }
      // Direct: just append
      else {
        stops.push(newStop);
      }
      return { ...f, stops };
    });
  }
  function removeStop(id) {
    setForm((f) => ({
      ...f,
      stops:    f.stops.filter((s) => s.id !== id),
      // Orphan any packages that were bound to this stop so they're reassignable
      packages: f.packages.map((p) => p.stopId === id ? { ...p, stopId: null } : p),
    }));
  }
  function updateStop(id, k, v) {
    // Functional setForm so each call sees the LATEST stops array, not
    // a closure snapshot. Critical when multiple updateStop calls fire
    // in sequence within one event (e.g. PlacesAutocomplete picking a
    // suggestion fires address + lat + lng back-to-back). With the old
    // closure-based version, the last call would overwrite the others
    // with the original snapshot's stops, silently dropping the address.
    setForm((prev) => ({
      ...prev,
      stops: prev.stops.map((s) => s.id === id ? { ...s, [k]: v } : s),
    }));
  }
  function moveStop(idx, dir) {
    const arr = [...form.stops];
    const to = idx + dir;
    if (to < 0 || to >= arr.length) return;
    [arr[idx], arr[to]] = [arr[to], arr[idx]];
    set("stops", arr);
  }
  function selectLocation(stopId, loc) {
    set("stops", form.stops.map((s) =>
      s.id === stopId ? {
        ...s,
        locationName: loc.name,
        address:      loc.address || "",
        lat:          loc.lat  || null,
        lng:          loc.lng  || null,
        // Carry the directory entry's id forward on the stop so later
        // surfaces (job detail, guard execution) can look up the
        // saved contacts for this address without re-matching by
        // string. A Google-picked place has no saved id yet (locationId
        // stays null → a new directory entry is created on save), but it
        // does carry a placeId — the canonical dedup key.
        locationId:   loc.id || null,
        placeId:      loc.placeId || null,
      } : s
    ));
  }

  // Note: Flight lookup, billing clock start/end, and the FlightAware integration
  // have all moved to the invoicing flow. Those calculations depend on *actual* flight
  // times (wheels-up / gate-in) which don't exist at job-creation time. The fields
  // `billingClockStart` / `billingClockEnd` remain on the job doc as empty strings
  // and get populated when the invoice is generated.

  // ── Guard assignment ──────────────────────────────────────
  function handleGuardAssign(guardNumber, guardId) {
    const guard = guards.find((g) => g.id === guardId);
    const rest  = form.guardAssignments.filter((g) => g.guardNumber !== guardNumber);
    if (guardId) {
      set("guardAssignments", [...rest, { guardNumber, guardId, guardName: guard?.name || "", payRate: guard?.defaultPayRate || "" }]);
    } else {
      set("guardAssignments", rest);
    }
  }
  function handleGuardPayRate(guardNumber, payRate) {
    set("guardAssignments", form.guardAssignments.map((g) =>
      g.guardNumber === guardNumber ? { ...g, payRate: parseFloat(payRate) || 0 } : g
    ));
  }
  // Own car (bills mileage) vs rides along (time only). Default follows the
  // legacy lead/chase rule (G1/G3 drive) until the owner overrides it.
  function handleGuardVehicle(guardNumber, ownCar) {
    set("guardAssignments", form.guardAssignments.map((g) =>
      g.guardNumber === guardNumber ? { ...g, ownCar } : g
    ));
  }

  // Summary line
  const assignedGuards = form.guardAssignments.filter((g) => g.guardId);
  // Count the cars the owner actually set, not the legacy G1/G3 rule. The
  // per-guard toggles above already drive the real billing through
  // guardOwnsCar; this line was still hardcoded to [1, 3], so a 3-guard job
  // with three cars read "2 mileage sets" and a G2-drives/G3-rides job read
  // the right number for the wrong reason.
  const mileageSets    = form.guardAssignments
    .filter((g) => g.guardId && g.guardNumber <= numGuards && guardOwnsCar(g, g.guardNumber))
    .length;
  const unassigned     = numGuards - assignedGuards.length;

  // ── Bulk split halves (-A / -B) ───────────────────────────
  // When the bulk preview has rows landing as guard halves, Step 3 can't use
  // the single global "number of guards" picker — each half is its own car
  // (route A → G1/G2, route B → G3/G4) with its own count from the per-row
  // "# guards" selector. Surface a slot per route so the owner can assign B's
  // driver (G3), which the flat 1–4 picker never exposes.
  const bulkSplitHalves = parsedBulkJobs
    .filter((j) => j._selected)
    .map((j) => {
      const rb = baseJobNumber(j.jobNumber);
      if (!(j._existingJobId && j.jobNumber !== rb && j._existingJobNumber === rb)) return null;
      const suffix = j.jobNumber.slice(rb.length).replace(/^-/, "").toUpperCase();
      return { suffix, jobNumber: j.jobNumber, count: j._guardCount || 1 };
    })
    .filter(Boolean);
  const isBulkSplit = bulkSplitHalves.length >= 1;
  // Build the route-grouped guard slots. Car mapping is fixed: A = G1/G2,
  // B = G3/G4. The passenger slot only appears when that half is set to 2+.
  const splitSlots = [];
  for (const suffix of ["A", "B"]) {
    const half = bulkSplitHalves.find((h) => h.suffix === suffix);
    if (!half) continue;
    const lead = suffix === "A" ? 1 : 3;
    splitSlots.push({ gNum: lead, title: `Route ${suffix} (${half.jobNumber}) — driver`, desc: "Time + mileage · photos" });
    if (half.count >= 2) splitSlots.push({ gNum: lead + 1, title: `Route ${suffix} — 2nd guard`, desc: "Time only · no mileage" });
  }

  // Plain bulk (2+ selected rows that aren't the A/B guard-half case):
  // Step 3's flat "number of guards" picker conflicts with the per-row
  // "# guards" selectors on Step 2 — at create, each job takes ITS OWN
  // count (numGuards: bj._guardCount) and the first N assigned guards.
  // So Step 3 renders slots for the LARGEST crew and summarizes per job
  // instead of showing a single misleading global count.
  const bulkMultiRows = !isBulkSplit ? parsedBulkJobs.filter((j) => j._selected) : [];
  const isBulkMulti = bulkMultiRows.length >= 2;

  // ── Merge into existing job ───────────────────────────────
  // Mirrors mergeJob()'s dedup in firestore.js so the chip preview agrees
  // with what the save will actually append. Returns the form stops/packages
  // split into "will be added" vs "already on the job" buckets.
  function computeMergePreview(job, stops, packages) {
    const norm = (s) => String(s || "").toLowerCase().replace(/\s+/g, " ").replace(/[.,;]+$/g, "").trim();
    const existingStops = Array.isArray(job?.stops) ? job.stops : [];
    const existingPackages = Array.isArray(job?.packages) ? job.packages : [];
    const existingStopKeys = new Set(existingStops.map((s) => `${norm(s.locationName)}|${norm(s.address)}`));
    const hasAirport = existingStops.some((s) => s.type === "airport");
    const existingHawbs = new Set(existingPackages.map((p) => norm(p.hawbNumber)).filter(Boolean));
    const newStops = [], dupStops = [];
    for (const s of (stops || [])) {
      if (s.type === "airport" && hasAirport) { dupStops.push(s); continue; }
      const key = `${norm(s.locationName)}|${norm(s.address)}`;
      if (existingStopKeys.has(key)) dupStops.push(s);
      else { newStops.push(s); existingStopKeys.add(key); }
    }
    const newPackages = [], dupPackages = [];
    for (const p of (packages || [])) {
      const hawb = norm(p.hawbNumber);
      if (hawb && existingHawbs.has(hawb)) dupPackages.push(p);
      else { newPackages.push(p); if (hawb) existingHawbs.add(hawb); }
    }
    return { newStops, dupStops, newPackages, dupPackages };
  }

  // Ferrari re-upload: a signed copy of an already-created job came back.
  // Advance the existing job's status from the signatures (delivery
  // confirmation → complete, shipper only → inProgress; advance-only) and
  // replace its POD with the signed copy. Never touches direction, stops,
  // or packages beyond the POD photo — the job already has the right shape.
  const [advancing, setAdvancing] = useState(false);
  async function handleAdvanceExisting() {
    if (!existingJobMatch || advancing) return;
    setAdvancing(true);
    setError("");
    try {
      const target = statusAdvanceFromSignatures(existingJobMatch.status, existingJobMatch._sigs);
      const patch = {};
      if (target) {
        patch.status = target;
        if (target === "complete" && !existingJobMatch.completedAt) {
          patch.completedAt = new Date().toISOString();
        }
      }
      // The signed copy supersedes whatever POD is on file (usually the
      // unsigned original fed in at creation) — overwrite, don't keep-if-set.
      const img = pastedImages.find((p) => (p.mediaType || "").startsWith("image/"));
      if (img) {
        const url = await uploadGuardPhoto(img.file, existingJobMatch.id, "package-doc", userProfile.id || "owner", "pod");
        patch.stops = (existingJobMatch.stops || []).map((s) =>
          s.type === "airport" ? s : { ...s, podPhotoUrl: url });
        if ((existingJobMatch.packages || []).length > 0) {
          patch.packages = existingJobMatch.packages.map((p) => ({ ...p, podPhotoUrl: url }));
        }
      }
      if (Object.keys(patch).length > 0) {
        await updateJob(existingJobMatch.id, patch);
      }
      await logAudit({
        jobId: existingJobMatch.id, companyId,
        action: `Signed Ferrari copy re-uploaded · ${existingJobMatch.jobNumber || ""}${target ? ` · status → ${target}` : ""}${img ? " · signed POD saved" : ""}`,
        userId: userProfile.id, userName: userProfile.name,
      });
      // Route the freshly parsed stops through the location directory —
      // the signed copy often carries contact info the original didn't
      // (a delivery-confirmation name, a second phone). mergeParsedContact
      // inside fills blanks / appends new numbers, never overwrites.
      (form.stops || []).filter((s) => s.type !== "airport").forEach((s) => {
        upsertLocationFromStop(companyId, s, { notes: form.notes }).catch((err) => {
          console.warn("Couldn't upsert location for stop", s, err);
        });
      });
      onCreated?.(`${existingJobMatch.jobNumber || "Job"} — ${target ? `marked ${target}` : "signed POD saved"}`);
    } catch (e) {
      console.error(e);
      setError("Failed to update the existing job. Please try again.");
      setAdvancing(false);
    }
  }

  async function handleMerge() {
    if (!existingJobMatch || merging) return;
    setMerging(true);
    setError("");
    try {
      const res = await mergeJob(
        existingJobMatch.id,
        { stops: form.stops, packages: form.packages },
        { id: userProfile.id, name: userProfile.name },
      );
      // Promote any genuinely-new stop into the Locations directory, same
      // as the create path. Fire-and-forget — don't block the close on it.
      (form.stops || []).forEach((s) => {
        upsertLocationFromStop(companyId, s, { notes: form.notes }).catch((err) => {
          console.warn("Couldn't upsert location for stop", s, err);
        });
      });
      onCreated?.(`${existingJobMatch.jobNumber} — merged (+${res.addedPackages} pkg, +${res.addedStops} stop${res.addedStops !== 1 ? "s" : ""})`);
    } catch (e) {
      console.error(e);
      setError("Failed to merge into existing job. Please try again.");
      setMerging(false);
    }
  }

  // ── Save ──────────────────────────────────────────────────
  async function handleSave() {
    // Job # is required only for Malca (their tracking system needs it).
    // Other brokers either don't issue a number (DAI) or issue them inconsistently.
    //
    // Bulk Malca mode is special: each parsed row carries its OWN jobNumber,
    // so the top-level form field is irrelevant. In that case validate per-row
    // instead of requiring a single "umbrella" number on the form.
    const isMalcaBroker = (form.brokerName || "").toLowerCase().includes("malca");
    const selectedBulkForValidation = parsedBulkJobs.filter((b) => b._selected);
    const isBulkSave = selectedBulkForValidation.length >= 2;
    if (isMalcaBroker && !isBulkSave && !form.jobNumber.trim()) {
      return setError("Job number is required for Malca jobs.");
    }
    if (isMalcaBroker && isBulkSave) {
      const missing = selectedBulkForValidation.find((bj) => !String(bj.jobNumber || "").trim());
      if (missing) {
        return setError("Every selected Malca row needs a job number. Fill in the missing ones before creating.");
      }
    }
    if (!form.brokerId)         return setError("Select a broker.");
    setSaving(true);
    setError("");
    try {
      // Slot order (G1, G2, …), not assignment order — bulk jobs slice the
      // first N of these for their own guard count, so G1 must come first
      // even when the owner filled G2's dropdown before G1's.
      const guardIds = [...form.guardAssignments]
        .sort((a, b) => (a.guardNumber || 0) - (b.guardNumber || 0))
        .map((g) => g.guardId).filter(Boolean);
      const status   = guardIds.length > 0 ? "confirmed" : "pool";

      // ── Bulk Malca (multiple independent jobs each with own job#) ────
      // No shared bundleId — each row is fully independent. Loop createJob
      // once per checked row using the row's own jobNumber/date/time/stops.
      // Shared fields (broker, dispatcher, guards) come from the form.
      const selectedBulk = parsedBulkJobs.filter((b) => b._selected);
      if (selectedBulk.length >= 2) {
        if (!form.brokerId) { setSaving(false); return setError("Select a broker."); }
        let createdCount = 0;
        let mergedCount = 0;
        // Merges that changed nothing. Reporting these as plain "merged" is
        // what hid a whole re-upload landing on the wrong jobs.
        let noopCount = 0;
        let movedCount = 0;
        // Placeholder ids already repurposed as a guard half (so the second
        // half spins off a new job instead of folding into the first).
        const claimedPlaceholders = new Set();
        for (const bj of selectedBulk) {
          // Route each row's direction through the same resolver as the
          // single-job path. Bulk Malca rows carry an explicit Direction
          // column (bj.jobType), so it resolves via the "stated" cascade
          // step — same answer as before, but one decision function.
          const bjType = resolveJobType({}, companyDoc || {}, {
            statedDirection: bj.jobType,
            aiSuggestedJobType: bj.jobType,
          }).jobType;
          const stopType = bjType === "inbound" ? "delivery" : "pickup";
          // Build the per-job stops array. Always seed an airport stop in
          // the right position so mileage/route logic downstream works.
          const realStops = (bj.stops || []).map((s) => ({
            id: uid(),
            type: s.type || stopType,
            locationName: s.locationName || "",
            address: s.address || "",
            lat: typeof s.lat === "number" ? s.lat : null,
            lng: typeof s.lng === "number" ? s.lng : null,
            scheduledTime: s.scheduledTime || bj.scheduledTime || "",
          }));
          const airportStop = {
            id: uid(),
            ...buildAirportStopFields(bjType),
            locationName: bj.airportCode || companyDoc?.hubAirport || "CLT",
          };
          const jobStops = bjType === "inbound"
            ? [airportStop, ...realStops]
            : [...realStops, airportStop];
          // Receipt rows the parser put on THIS job. Bulk rows used to be
          // created with packages: [] — fine for a plain schedule table that
          // has none, but a split dispatch carries them in its detail block,
          // and dropping them meant every receipt number and weight had to be
          // retyped. stopIndex is into realStops, so map through those ids.
          const jobPackages = (bj.packages || []).map((pk) => {
            const si = Number(pk.stopIndex);
            const target = Number.isInteger(si) && realStops[si] ? realStops[si] : realStops[0];
            return {
              id: uid(),
              hawbNumber:    pk.hawbNumber    || "",
              description:   pk.description   || "",
              declaredValue: pk.declaredValue || "",
              weight:        pk.weight        || "",
              stopId:        target ? target.id : null,
            };
          }).filter((pk) => pk.stopId);

          // A suffixed dispatch half (2922A/2922B) that base-matched a bulk-
          // added placeholder (2922): land it as its own guard job. The FIRST
          // half repurposes the placeholder → {base}-A (merging its stub stops
          // in so nothing's lost); the SECOND spins off a new {base}-B. This
          // keeps Malca's A/B split instead of folding both into the base.
          const rowBase = baseJobNumber(bj.jobNumber);
          const isSuffixed = !!bj.jobNumber && bj.jobNumber !== rowBase;
          if (bj._existingJobId && isSuffixed && bj._existingJobNumber === rowBase) {
            const suffix = bj.jobNumber.slice(rowBase.length).replace(/^-/, "").toUpperCase();
            // Each half is its own job and can have 1 OR 2 guards (its own lead
            // + optional passenger). Map by CAR: route A = G1(+G2), route B =
            // G3(+G4). Renumber to slots 1/2 within the half; the half's guard
            // count is however many of its car's slots are actually assigned.
            // Guard count comes from THIS row's "# guards" selector. Pull who
            // from Step 3 by car (route A = G1/G2, route B = G3/G4); the
            // passenger is only used when the row is set to 2+ guards.
            const sNum = Math.max(1, bj._guardCount || 1);
            const carLeadNum = suffix === "B" ? 3 : 1;
            const carPassNum = suffix === "B" ? 4 : 2;
            const lead = form.guardAssignments.find((g) => g.guardNumber === carLeadNum && g.guardId);
            const pass = sNum >= 2 ? form.guardAssignments.find((g) => g.guardNumber === carPassNum && g.guardId) : null;
            const sGA = [];
            if (lead) sGA.push({ guardNumber: 1, guardId: lead.guardId, guardName: lead.guardName || "", payRate: lead.payRate || "" });
            if (pass) sGA.push({ guardNumber: 2, guardId: pass.guardId, guardName: pass.guardName || "", payRate: pass.payRate || "" });
            const sGuardIds = sGA.map((g) => g.guardId);
            const sStatus = sGuardIds.length ? "confirmed" : "pool";
            if (!claimedPlaceholders.has(bj._existingJobId)) {
              // eslint-disable-next-line no-await-in-loop
              await mergeJob(bj._existingJobId, { stops: jobStops, packages: [] }, { id: userProfile.id, name: userProfile.name });
              // eslint-disable-next-line no-await-in-loop
              await updateJob(bj._existingJobId, {
                jobNumber: `${rowBase}-${suffix}`, subJobLabel: `Guard ${suffix}`,
                numGuards: sNum, guardAssignments: sGA, guardIds: sGuardIds, status: sStatus,
              });
              claimedPlaceholders.add(bj._existingJobId);
              createdCount++;
            } else {
              // eslint-disable-next-line no-await-in-loop
              const ref = await createJob({
                ...form,
                jobNumber: `${rowBase}-${suffix}`,
                subJobLabel: `Guard ${suffix}`,
                parentJobId: bj._existingJobId,
                jobType: bjType,
                airportCode: bj.airportCode || form.airportCode || "CLT",
                scheduledDate: bj.scheduledDate || "",
                scheduledTime: bj.scheduledTime || "",
                stops: jobStops,
                packages: [],
                companyId,
                numGuards: sNum,
                guardAssignments: sGA,
                guardIds: sGuardIds,
                status: sStatus,
                createdBy: userProfile.id,
              });
              // eslint-disable-next-line no-await-in-loop
              await logAudit({ jobId: ref.id, companyId, action: `Bulk split half created · ${rowBase}-${suffix} · ${bj.scheduledDate} · ${bjType}`, userId: userProfile.id, userName: userProfile.name });
              createdCount++;
            }
            jobStops.forEach((s) => { upsertLocationFromStop(companyId, s, { notes: form.notes }).catch((err) => {
              console.warn("Couldn't upsert location for stop", s, err);
            }); });
            continue;
          }

          // Row whose job number already exists → merge its stops into the
          // existing job instead of creating a duplicate. mergeJob folds the
          // airport stop into the existing one and dedupes the rest.
          if (bj._existingJobId) {
            // eslint-disable-next-line no-await-in-loop
            const mres = await mergeJob(
              bj._existingJobId,
              {
                stops: jobStops,
                packages: jobPackages,
                // Let a corrected re-send move the job. The row's date comes
                // straight from the dispatcher's table, so it's the intended
                // one when it differs from what the job already has.
                scheduledDate: bj.scheduledDate || null,
                scheduledTime: bj.scheduledTime || null,
              },
              { id: userProfile.id, name: userProfile.name },
            );
            if (mres?.noop) noopCount++;
            if (mres?.movedFrom) movedCount++;
            jobStops.forEach((s) => { upsertLocationFromStop(companyId, s, { notes: form.notes }).catch((err) => {
              console.warn("Couldn't upsert location for stop", s, err);
            }); });
            mergedCount++;
            continue;
          }

          // People named on THIS row (step 2), trimmed to its guard count.
          // guardAssignments has to be rebuilt to match — the app reads names
          // and pay rates from there, and `...form` would otherwise carry the
          // shared crew alongside this row's guardIds.
          const rowGuardIds = (bj._guardIds || [])
            .slice(0, bj._guardCount || 1)
            .filter(Boolean);
          const rowAssignments = rowGuardIds.map((gid, i) => {
            const g = guards.find((x) => x.id === gid);
            // Rate typed against THIS job's slot, else anything set for the
            // same person on the shared crew, else their profile default.
            const shared = form.guardAssignments.find((a) => a.guardId === gid);
            const ownRate = (bj._guardRates || [])[i];
            return {
              guardNumber: i + 1,
              guardId: gid,
              guardName: g?.name || shared?.guardName || "",
              payRate: ownRate || shared?.payRate || g?.payRate || "",
            };
          });

          // eslint-disable-next-line no-await-in-loop
          const ref = await createJob({
            ...form,
            ...(rowAssignments.length ? { guardAssignments: rowAssignments } : {}),
            jobNumber: bj.jobNumber || "",
            jobType: bjType,
            airportCode: bj.airportCode || form.airportCode || "CLT",
            scheduledDate: bj.scheduledDate || "",
            scheduledTime: bj.scheduledTime || "",
            stops: jobStops,
            packages: jobPackages,
            companyId,
            numGuards: bj._guardCount || numGuards,
            // This row's own people when the owner named them, else the shared
            // step-3 crew as before. Per-row wins because the shared crew is
            // sliced from the front for every row — fine for independent jobs
            // on different days, double-booking on an A/B split that runs at
            // the same hour.
            guardIds: rowGuardIds.length
              ? rowGuardIds
              : guardIds.slice(0, bj._guardCount || numGuards),
            status: rowGuardIds.length ? "confirmed" : status,
            createdBy: userProfile.id,
          });
          // eslint-disable-next-line no-await-in-loop
          await logAudit({
            jobId: ref.id, companyId,
            action: `Bulk Malca job created · ${bj.jobNumber || "(no #)"} · ${bj.scheduledDate} · ${bjType}`,
            userId: userProfile.id, userName: userProfile.name,
          });
          jobStops.forEach((s) => { upsertLocationFromStop(companyId, s, { notes: form.notes }).catch((err) => {
          console.warn("Couldn't upsert location for stop", s, err);
        }); });
          createdCount++;
        }
        onCreated?.(
          mergedCount
            ? `${createdCount} bulk job${createdCount !== 1 ? "s" : ""}, ${mergedCount} merged`
              + (movedCount ? ` (${movedCount} re-dated)` : "")
              + (noopCount ? ` — ⚠ ${noopCount} added nothing, already on the job` : "")
            : `${createdCount} bulk jobs`
        );
        return;
      }

      // ── Multi-detail (DAI schedule) ─────────────────────────────────
      // One email → N jobs, all sharing a bundleId. Each detail row becomes
      // its own job with its own (origin, destination) stops. The shared
      // fields (broker, rep, dispatcher) are copied to every job.
      const selectedDetails = parsedDetails.filter((d) => d._selected);
      if (selectedDetails.length >= 2) {
        if (!form.brokerId) { setSaving(false); return setError("Select a broker."); }
        const bundleId = `bundle_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        let createdCount = 0;
        for (const d of selectedDetails) {
          // Build the two stops for this detail. normalizeStopTypes will set
          // pickup/delivery on save so we just give them sensible defaults.
          const stops = [
            {
              id: uid(),
              type: "pickup",
              // Carry locationId through so upsertLocationFromStop can
              // update the EXISTING saved row (incl. any rename) rather
              // than creating a duplicate when the parser-matched name
              // got edited in the modal.
              locationId: d.fromLocationId || null,
              locationName: d.fromName || "",
              address: d.fromAddress || "",
              lat: typeof d.fromLat === "number" ? d.fromLat : null,
              lng: typeof d.fromLng === "number" ? d.fromLng : null,
              scheduledTime: "",
            },
            {
              id: uid(),
              type: "delivery",
              locationId: d.outOfTown ? null : (d.toLocationId || null),
              locationName: d.outOfTown ? "OUT OF TOWN" : (d.toName || ""),
              address: d.outOfTown ? "" : (d.toAddress || ""),
              lat: d.outOfTown ? null : (typeof d.toLat === "number" ? d.toLat : null),
              lng: d.outOfTown ? null : (typeof d.toLng === "number" ? d.toLng : null),
              scheduledTime: "",
              outOfTown: !!d.outOfTown,
            },
          ];
          // Merge per-trip notes (parsed from this specific row) onto
          // the shared form.notes. Each job in a multi-detail save
          // gets its own notes field so per-trip caveats — alt dates,
          // handoff offers — stay attached to the right job.
          const tripNotes = (d.notes || "").trim();
          const mergedNotes = tripNotes
            ? (form.notes ? `${form.notes}\n\n${tripNotes}` : tripNotes)
            : (form.notes || "");
          // eslint-disable-next-line no-await-in-loop
          const ref = await createJob({
            ...form,
            jobType: "direct",
            scheduledDate: d.scheduledDate || "",
            scheduledTime: d.scheduledTime || "",
            stops,
            packages: [],
            airportCode: "",
            bundleId,
            companyId,
            numGuards,
            guardIds,
            status,
            notes: mergedNotes,
            createdBy: userProfile.id,
          });
          // eslint-disable-next-line no-await-in-loop
          await logAudit({
            jobId: ref.id, companyId,
            action: `DAI bundled job created · ${form.brokerName} · rep ${form.salesRepName || "—"} · ${d.scheduledDate}`,
            userId: userProfile.id, userName: userProfile.name,
          });
          stops.forEach((s) => { upsertLocationFromStop(companyId, s, { notes: form.notes }).catch((err) => {
          console.warn("Couldn't upsert location for stop", s, err);
        }); });
          createdCount++;
        }
        onCreated?.(`${createdCount} bundled jobs`);
        return;
      }

      // ── Single-job flow ─────────────────────────────────────────────
      // markCompleteOnSave (Ferrari/Executive waybill flow): the trip
      // already ran on paper, so the job skips the guard workflow and is
      // born complete — it surfaces on Ready to bill immediately.
      const bornComplete = saveAttachmentAsDoc && markCompleteOnSave;
      const ref = await createJob({
        ...form,
        companyId,
        numGuards,
        guardIds,
        status: bornComplete ? "complete" : status,
        ...(bornComplete ? { completedAt: new Date().toISOString() } : {}),
        createdBy: userProfile.id,
      });
      await logAudit({
        jobId:    ref.id,
        companyId,
        action:   `Job created by owner · ${form.jobNumber} · ${form.brokerName} · ${guardIds.length} guard${guardIds.length !== 1 ? "s" : ""}${bornComplete ? " · marked complete (waybill on file)" : ""}`,
        userId:   userProfile.id,
        userName: userProfile.name,
      });
      // Persist the parsed attachment as the job's package document (POD).
      // Uploaded after create (the storage path needs the job id), then
      // stamped on every package and every real stop that doesn't already
      // have a photo — that satisfies the POD requirement at billing time
      // without a re-upload. Non-fatal: if the upload fails the job still
      // exists and the owner can add the photo in the billing Review.
      if (saveAttachmentAsDoc) {
        try {
          const img = pastedImages.find((p) => (p.mediaType || "").startsWith("image/"));
          if (img) {
            const url = await uploadGuardPhoto(img.file, ref.id, "package-doc", userProfile.id || "owner", "pod");
            const patch = {
              stops: (form.stops || []).map((s) =>
                s.type === "airport" ? s : { ...s, podPhotoUrl: s.podPhotoUrl || url }),
            };
            if ((form.packages || []).length > 0) {
              patch.packages = form.packages.map((p) => ({ ...p, podPhotoUrl: p.podPhotoUrl || url }));
            }
            await updateJob(ref.id, patch);
            await logAudit({
              jobId: ref.id, companyId,
              action: "Parsed attachment saved as package document (POD)",
              userId: userProfile.id, userName: userProfile.name,
            });
          }
        } catch (err) {
          console.warn("Couldn't save the parsed attachment as package document", err);
        }
      }
      // Auto-save each real stop as a reusable Location (dedupes by name+address,
      // bumps jobCount on match). Pass notes through so any phone number
      // mentioned in the special-instructions text gets seeded as a
      // contact on a newly-created location. Fire-and-forget — don't
      // block UI on it.
      (form.stops || []).forEach((s) => {
        upsertLocationFromStop(companyId, s, { notes: form.notes }).catch((err) => {
          console.warn("Couldn't upsert location for stop", s, err);
        });
      });
      onCreated?.(form.jobNumber);
    } catch (e) {
      console.error(e);
      setError("Failed to create job. Please try again.");
      setSaving(false);
    }
  }

  // ── Render ────────────────────────────────────────────────
  return (
    <>
      <div className="modal-overlay" style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
        zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}>
        <div className="modal-card" style={{
          background: "#fff", borderRadius: 12, width: "100%", maxWidth: 660,
          maxHeight: "92vh", overflow: "hidden", display: "flex", flexDirection: "column",
          boxShadow: "0 8px 40px rgba(0,0,0,0.22)",
        }}>
          {/* Header */}
          <div style={{
            padding: "14px 20px", borderBottom: "1px solid #e8e8e8",
            display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0,
          }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700 }}>Create new job</div>
              <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                Step {step} of 3 — {step === 1 ? "Dispatcher email" : step === 2 ? "Job details" : "Guard assignment"}
              </div>
            </div>
            <button onClick={onClose}
              style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#888", lineHeight: 1, padding: "2px 6px" }}>
              ✕
            </button>
          </div>

          {/* Progress bar */}
          <div style={{ display: "flex", padding: "10px 20px", gap: 6, borderBottom: "1px solid #f0f0f0", flexShrink: 0 }}>
            {[1, 2, 3].map((s) => (
              <div key={s} style={{
                flex: 1, height: 3, borderRadius: 2,
                background: s <= step ? "#185FA5" : "#e8e8e8",
                transition: "background 0.2s",
              }} />
            ))}
          </div>

          {/* Scrollable body */}
          <div style={{ flex: 1, overflowY: "auto", padding: "20px 22px" }}>

            {/* ══ STEP 1: EMAIL ══ */}
            {step === 1 && (
              <div
                // The whole step is a drop target — attachments dragged from
                // an email or from Explorer land anywhere on the panel.
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{
                  display: "flex", flexDirection: "column", gap: 14,
                  outline: dragOver ? "2px dashed #185FA5" : "2px dashed transparent",
                  outlineOffset: 4, borderRadius: 8,
                  background: dragOver ? "#F0F7FF" : "transparent",
                  transition: "background 0.15s",
                }}
              >
                <div style={{
                  background: "#E6F1FB", borderLeft: "3px solid #185FA5",
                  padding: "9px 13px", borderRadius: "0 7px 7px 0", fontSize: 11, color: "#0C3976", lineHeight: 1.65,
                }}>
                  Paste the dispatcher email text <strong>and/or a screenshot</strong> of the job table —
                  or <strong>drag an attachment</strong> (image or PDF) onto this panel.
                  Screenshots parse much more accurately for tabular data. Review all details before releasing.
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{ fontSize: 11, fontWeight: 600, color: "#444" }}>
                    Dispatcher email <span style={{ color: "#888", fontWeight: 400 }}>— paste text, Ctrl+V a screenshot, or drop a file here</span>
                  </label>
                  <textarea
                    value={rawEmail}
                    onChange={(e) => setRawEmail(e.target.value)}
                    onPaste={handleImagePaste}
                    placeholder="Paste the full email here — or paste a screenshot directly (Ctrl+V)..."
                    style={{ minHeight: 180, fontSize: 11, lineHeight: 1.7, fontFamily: "monospace" }}
                  />
                </div>

                {/* Attached images */}
                {pastedImages.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {pastedImages.map((img) => (
                      <div key={img.id} style={{ position: "relative", border: "1px solid #ddd", borderRadius: 6, padding: 3, background: "#f8f9fb" }}>
                        {img.mediaType === "application/pdf" ? (
                          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 12px", maxWidth: 160 }}>
                            <span style={{ fontSize: 20 }}>📄</span>
                            <span style={{ fontSize: 10, color: "#555", wordBreak: "break-all" }}>{img.name || "attachment.pdf"}</span>
                          </div>
                        ) : (
                          <img src={img.preview} alt="email screenshot"
                            style={{ display: "block", maxWidth: 160, maxHeight: 120, objectFit: "contain", borderRadius: 4 }} />
                        )}
                        <button
                          onClick={() => removeImage(img.id)}
                          title="Remove image"
                          style={{
                            position: "absolute", top: -6, right: -6, width: 20, height: 20,
                            borderRadius: "50%", border: "none", background: "#a32d2d", color: "#fff",
                            fontSize: 11, fontWeight: 700, cursor: "pointer", lineHeight: "20px", padding: 0,
                          }}
                        >×</button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Image upload button + hidden input */}
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  style={{ display: "none" }}
                  onChange={(e) => { addImageFiles(e.target.files); e.target.value = ""; }}
                />
                <button
                  onClick={() => imageInputRef.current?.click()}
                  style={{
                    alignSelf: "flex-start", background: "none", border: "1px dashed #bbb",
                    color: "#555", fontSize: 11, cursor: "pointer", padding: "6px 12px", borderRadius: 6,
                  }}
                >
                  📎 Attach screenshot or PDF{pastedImages.length > 0 ? " (add another)" : ""}
                </button>

                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <button
                    className="btn btn-blue"
                    onClick={handleParseEmail}
                    disabled={parsing || (!rawEmail.trim() && pastedImages.length === 0)}
                  >
                    {parsing
                      ? "Parsing..."
                      : pastedImages.length > 0 && rawEmail.trim()
                        ? "✦ Parse email + screenshot"
                        : pastedImages.length > 0
                          ? `✦ Parse ${pastedImages.length === 1 ? "screenshot" : "screenshots"}`
                          : "✦ Parse email with AI"}
                  </button>
                  <button
                    onClick={() => { set("dispatcherEmailRaw", rawEmail); setStep(2); }}
                    style={{ background: "none", border: "none", color: "#185FA5", fontSize: 12, cursor: "pointer", textDecoration: "underline", padding: 0 }}
                  >
                    Skip — enter manually
                  </button>
                </div>
                {error && <ErrorBar>{error}</ErrorBar>}
              </div>
            )}

            {/* ══ STEP 2: JOB DETAILS ══ */}
            {step === 2 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {showParseWarning && (
                  <div style={{
                    background: "#fff3cd", borderLeft: "3px solid #EF9F27",
                    padding: "9px 13px", borderRadius: "0 7px 7px 0", fontSize: 11, color: "#633806", lineHeight: 1.65,
                  }}>
                    ⚠️ AI parsing can make mistakes — review every field before continuing.
                  </div>
                )}

                {/* Document checks (Ferrari + Malca) — the numbers on the
                    document don't agree with each other, or look off.
                    Advisory; the fields below are still editable. */}
                {parseWarnings.length > 0 && (
                  <div style={{
                    background: "#FFF7E0", border: "1px solid #F0D88C",
                    borderRadius: 8, padding: "9px 13px", fontSize: 11, color: "#74540C", lineHeight: 1.6,
                  }}>
                    <strong>⚠ Check the document:</strong>
                    {parseWarnings.map((w, i) => <div key={i}>• {w}</div>)}
                  </div>
                )}

                {/* Direction couldn't be confidently resolved — the cascade
                    fell through to its fallback (or a Ferrari form had no
                    usable address block). Ask the owner to confirm the
                    inbound/outbound/direct toggle below before saving. */}
                {directionReview && (
                  <div style={{
                    background: "#FDECEA", border: "1px solid #F5C6C0",
                    borderRadius: 8, padding: "9px 13px", fontSize: 11, color: "#8a2c20", lineHeight: 1.6,
                  }}>
                    🧭 <strong>Confirm direction.</strong> I couldn't determine inbound vs. outbound
                    from this document with confidence{directionReview.source === "ferrari_no_block" ? " (no readable pickup/consignee block on the form)" : ""}.
                    Defaulted to <strong>{(form.jobType || "outbound")}</strong> — double-check the Job type toggle below.
                  </div>
                )}

                {/* Ferrari/Executive: keep the parsed attachment on the job as
                    its package document (POD), optionally marking the job
                    complete so an already-ran trip goes straight to Ready to
                    bill. Single-job path only — bulk/multi-detail saves can't
                    map one attachment to many jobs. */}
                {pastedImages.length > 0
                  && parsedBulkJobs.length < 2 && parsedDetails.length < 2
                  && ["ferrari", "executive"].some((k) =>
                       (form.brokerName || "").toLowerCase().includes(k)
                       || (form.dispatcherName || "").toLowerCase().includes(k))
                  && (() => {
                  const hasImg = pastedImages.some((p) => (p.mediaType || "").startsWith("image/"));
                  if (!hasImg) {
                    return (
                      <div style={{ fontSize: 11, color: "#888", background: "#f8f8f8", borderRadius: 7, padding: "8px 12px" }}>
                        📄 PDF attachments can't be saved as the package document yet — attach a screenshot
                        of the waybill if you want it kept on the job as the POD.
                      </div>
                    );
                  }
                  const datePast = !!form.scheduledDate && form.scheduledDate <= todayYmd();
                  return (
                    <div style={{ background: "#F3F9F3", border: "1px solid #CBE3CB", borderRadius: 8, padding: "10px 13px", display: "flex", flexDirection: "column", gap: 7 }}>
                      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#1d5c1d", cursor: "pointer" }}>
                        <input
                          type="checkbox"
                          checked={saveAttachmentAsDoc}
                          onChange={(e) => {
                            setSaveAttachmentAsDoc(e.target.checked);
                            if (!e.target.checked) setMarkCompleteOnSave(false);
                          }}
                        />
                        <span>📄 Save the parsed attachment as this job's <strong>package document (POD)</strong></span>
                      </label>
                      {saveAttachmentAsDoc && datePast && (
                        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#1d5c1d", cursor: "pointer", marginLeft: 22 }}>
                          <input
                            type="checkbox"
                            checked={markCompleteOnSave}
                            onChange={(e) => setMarkCompleteOnSave(e.target.checked)}
                          />
                          <span>Trip already ran — mark <strong>complete</strong> so it goes straight to Ready to bill</span>
                        </label>
                      )}
                    </div>
                  );
                })()}

                {/* Duplicate-job chip — a job with this number already exists.
                    Offer to MERGE the parsed stops/packages into it (deduped)
                    instead of creating a second job with the same number.
                    Only shown on the single-job path. */}
                {existingJobMatch && parsedBulkJobs.length < 2 && parsedDetails.length < 2 && (() => {
                  const preview = computeMergePreview(existingJobMatch, form.stops, form.packages);
                  const nPkg = preview.newPackages.length;
                  const nStop = preview.newStops.length;
                  const dupPkg = preview.dupPackages.length;
                  const dupStop = preview.dupStops.length;
                  const nothingNew = nPkg === 0 && nStop === 0;
                  // Ferrari re-upload: parse signatures rode along on the
                  // match. Offer a status-advance + signed-POD update as
                  // the primary action — the usual case is the signed
                  // copy coming back after the run.
                  const sigs = existingJobMatch._sigs;
                  const sigSigned = !!(sigs?.shipperSigned || sigs?.deliveryConfirmationSigned);
                  const advanceTarget = sigs ? statusAdvanceFromSignatures(existingJobMatch.status, sigs) : null;
                  const hasImgForPod = pastedImages.some((p) => (p.mediaType || "").startsWith("image/"));
                  const showAdvance = sigSigned && (advanceTarget || hasImgForPod);
                  return (
                    <div style={{
                      background: "#FFF7E0", border: "1px solid #F0D88C",
                      borderRadius: 8, padding: "11px 13px", display: "flex", flexDirection: "column", gap: 8,
                    }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#74540C" }}>
                        ⚠ {existingJobMatch.jobNumber} already exists — merge into it?
                      </div>
                      <div style={{ fontSize: 11, color: "#633806", lineHeight: 1.6 }}>
                        A job with this number is already uploaded
                        {existingJobMatch.status ? ` (status: ${existingJobMatch.status})` : ""}.
                        {nothingNew ? (
                          <> Nothing new to append — every parsed stop and package is already on it.</>
                        ) : (
                          <> Merging will append{" "}
                            {nPkg > 0 && <strong>{nPkg} package{nPkg !== 1 ? "s" : ""}</strong>}
                            {nPkg > 0 && nStop > 0 && " and "}
                            {nStop > 0 && <strong>{nStop} stop{nStop !== 1 ? "s" : ""}</strong>}
                            .{(dupPkg > 0 || dupStop > 0) && (
                              <span style={{ color: "#8a6d3b" }}>
                                {" "}({dupPkg > 0 ? `${dupPkg} package${dupPkg !== 1 ? "s" : ""}` : ""}
                                {dupPkg > 0 && dupStop > 0 ? " and " : ""}
                                {dupStop > 0 ? `${dupStop} stop${dupStop !== 1 ? "s" : ""}` : ""} already there, skipped)
                              </span>
                            )}
                          </>
                        )}
                      </div>
                      {/* Preview of the new rows so the owner sees exactly what lands. */}
                      {!nothingNew && (
                        <div style={{ fontSize: 10, color: "#74540C", display: "flex", flexDirection: "column", gap: 2 }}>
                          {preview.newStops.map((s, i) => (
                            <div key={`s${i}`}>+ stop · {s.locationName || "(unnamed)"}{s.address ? ` — ${s.address}` : ""}</div>
                          ))}
                          {preview.newPackages.map((p, i) => (
                            <div key={`p${i}`}>+ pkg · {p.hawbNumber || `(no ${pkgLabel})`}{p.description ? ` — ${p.description}` : ""}</div>
                          ))}
                        </div>
                      )}
                      {showAdvance && (
                        <div style={{ fontSize: 11, color: "#1d5c1d", background: "#F3F9F3", border: "1px solid #CBE3CB", borderRadius: 7, padding: "7px 10px" }}>
                          ✍ <strong>Signed copy detected</strong>
                          {sigs?.deliveryConfirmationSigned ? " — delivery confirmation signed" : " — shipper signed"}.
                          {advanceTarget
                            ? <> This job can be marked <strong>{advanceTarget === "complete" ? "complete" : "in progress"}</strong>{advanceTarget === "complete" ? " (goes to Ready to bill)" : ""}.</>
                            : <> Status is already {existingJobMatch.status} — only the POD photo will be replaced with the signed copy.</>}
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {showAdvance && (
                          <button
                            className="btn btn-blue btn-sm"
                            onClick={handleAdvanceExisting}
                            disabled={advancing || merging}
                          >
                            {advancing ? "Updating…"
                              : advanceTarget
                                ? `↻ Mark ${advanceTarget === "complete" ? "complete" : "in progress"}${hasImgForPod ? " + save signed POD" : ""}`
                                : "↻ Save signed POD"}
                          </button>
                        )}
                        <button
                          className={`btn btn-sm ${showAdvance ? "" : "btn-blue"}`}
                          onClick={handleMerge}
                          disabled={merging || advancing || nothingNew}
                          title={nothingNew ? "Nothing new to merge" : undefined}
                        >
                          {merging ? "Merging…" : `↻ Merge into ${existingJobMatch.jobNumber}`}
                        </button>
                        <button
                          className="btn btn-sm"
                          onClick={() => setExistingJobMatch(null)}
                          disabled={merging || advancing}
                        >
                          Create new anyway
                        </button>
                      </div>
                      {error && <ErrorBar>{error}</ErrorBar>}
                    </div>
                  );
                })()}

                {/* Bulk-Malca preview — multiple independent jobs each with
                    their own job #. Each row creates one full job on Save.
                    Same broker, airport, and direction across the batch. */}
                {parsedBulkJobs.length >= 2 && (
                  <div style={{
                    background: "#fff", border: "1px solid #d6e0ee", borderRadius: 8,
                    padding: 0, overflow: "hidden",
                  }}>
                    <div style={{
                      padding: "10px 14px", background: "#eef3fb", borderBottom: "1px solid #d6e0ee",
                      fontSize: 12, fontWeight: 700, color: "#185FA5",
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                    }}>
                      <span>📋 {(() => {
                        const sel = parsedBulkJobs.filter((j) => j._selected);
                        const isSplit = (j) => {
                          const rb = baseJobNumber(j.jobNumber);
                          return j._existingJobId && j.jobNumber !== rb && j._existingJobNumber === rb;
                        };
                        const splits = sel.filter(isSplit).length;
                        const merges = sel.filter((j) => j._existingJobId && !isSplit(j)).length;
                        const creates = sel.length - merges - splits;
                        const parts = [];
                        if (creates) parts.push(`${creates} new job${creates !== 1 ? "s" : ""}`);
                        if (splits) parts.push(`${splits} land as guard halves (-A / -B)`);
                        if (merges) parts.push(`${merges} merge into an existing job`);
                        return parts.length
                          ? `${parsedBulkJobs.length} detected — ${parts.join(" · ")}`
                          : `${parsedBulkJobs.length} jobs detected`;
                      })()}</span>
                      <span style={{ fontSize: 11, fontWeight: 500, color: "#666" }}>
                        {parsedBulkJobs.filter((j) => j._selected).length} selected
                      </span>
                    </div>
                    <div style={{ maxHeight: 480, overflowY: "auto" }}>
                      {parsedBulkJobs.map((bj, i) => {
                        const update = (patch) => setParsedBulkJobs((arr) =>
                          arr.map((x, j) => j === i ? { ...x, ...patch } : x)
                        );
                        const updateStop = (stopIdx, patch) => setParsedBulkJobs((arr) =>
                          arr.map((x, j) => j !== i ? x : ({
                            ...x,
                            stops: (x.stops || []).map((st, si) => si === stopIdx ? { ...st, ...patch } : st),
                          }))
                        );
                        // Drop a stop the parser put on the wrong job. Packages
                        // address their stop BY INDEX, so any bound to this one
                        // go with it and everything after shifts down — leave
                        // them and the receipts land on the wrong consignee.
                        const removeStop = (stopIdx) => setParsedBulkJobs((arr) =>
                          arr.map((x, j) => j !== i ? x : ({
                            ...x,
                            stops: (x.stops || []).filter((_, si) => si !== stopIdx),
                            packages: (x.packages || [])
                              .filter((pk) => Number(pk.stopIndex) !== stopIdx)
                              .map((pk) => {
                                const psi = Number(pk.stopIndex);
                                return Number.isInteger(psi) && psi > stopIdx
                                  ? { ...pk, stopIndex: psi - 1 }
                                  : pk;
                              }),
                          }))
                        );
                        return (
                          <div key={i} style={{
                            borderBottom: "1px solid #f0f0f0",
                            background: bj._selected ? "#fff" : "#fafafa",
                            opacity: bj._selected ? 1 : 0.55,
                          }}>
                            <div className="jcm-parsed-row" style={{
                              display: "grid",
                              gridTemplateColumns: "24px 110px 110px 70px 1fr 56px 22px",
                              gap: 8, padding: "8px 14px", alignItems: "center", fontSize: 11,
                            }}>
                              <input type="checkbox" checked={!!bj._selected}
                                onChange={(e) => update({ _selected: e.target.checked })} />
                              <input value={bj.jobNumber || ""}
                                onChange={(e) => update({ jobNumber: e.target.value })}
                                placeholder="MAKY26-XXXX"
                                style={{ fontSize: 11, fontFamily: "monospace", fontWeight: 600 }} />
                              <input type="date" value={bj.scheduledDate || ""}
                                onChange={(e) => update({ scheduledDate: e.target.value })}
                                style={{ fontSize: 11 }} />
                              <Time24Field value={bj.scheduledTime || ""}
                                onChange={(v) => update({ scheduledTime: v })}
                                style={{ fontSize: 11 }} />
                              <div style={{ fontSize: 11, color: "#444", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {(bj.stops || []).map((s) => s.locationName).filter(Boolean).join(" · ") || <span style={{ color: "#bbb" }}>no stops</span>}
                                {(bj.stops || []).length > 1 && (
                                  <span style={{ marginLeft: 6, fontSize: 9, color: "#a36b1d", fontWeight: 700 }}>
                                    · {bj.stops.length} pickups
                                  </span>
                                )}
                                {bj._existingJobId && (() => {
                                  const rb = baseJobNumber(bj.jobNumber);
                                  const isSplitHalf = bj.jobNumber !== rb && bj._existingJobNumber === rb;
                                  const suffix = isSplitHalf ? bj.jobNumber.slice(rb.length).replace(/^-/, "").toUpperCase() : "";
                                  return (
                                    <span
                                      title={isSplitHalf
                                        ? `${rb} already exists — this lands as its own guard job ${rb}-${suffix} (no duplicate).`
                                        : "A job with this number already exists — its stops merge in instead of creating a duplicate."}
                                      style={{
                                        marginLeft: 6, fontSize: 9, color: "#74540C", fontWeight: 700,
                                        background: "#FFF7E0", border: "1px solid #F0D88C",
                                        borderRadius: 4, padding: "1px 5px",
                                      }}
                                    >
                                      {isSplitHalf ? `↳ ${rb} → ${rb}-${suffix}` : "↻ exists — will merge"}
                                    </span>
                                  );
                                })()}
                              </div>
                              <select
                                value={bj._guardCount || 1}
                                onChange={(e) => update({ _guardCount: Number(e.target.value) })}
                                title="How many guards on this job"
                                style={{ fontSize: 11, padding: "2px 1px" }}
                              >
                                {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}🧍</option>)}
                              </select>
                              <button
                                onClick={() => update({ _expanded: !bj._expanded })}
                                title={bj._expanded ? "Collapse" : "Edit stops"}
                                style={{ background: "none", border: "1px solid #ddd", borderRadius: 4, padding: "1px 4px", cursor: "pointer", fontSize: 11, color: "#555" }}
                              >{bj._expanded ? "▴" : "▾"}</button>
                            </div>
                            {bj._expanded && (
                              <div style={{ padding: "8px 14px 12px 46px", background: "#fafbfc", display: "flex", flexDirection: "column", gap: 8 }}>
                                {/* Who works THIS row. The crew on step 3 is
                                    shared across every row, which is right for
                                    independent jobs on different days but wrong
                                    for an A/B split running at the same hour —
                                    there it put the same person on both halves.
                                    Set someone here and this row uses them
                                    instead; leave blank and step 3 applies as
                                    before, so a 23-row drop needs no extra
                                    clicks. */}
                                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                  {Array.from({ length: bj._guardCount || 1 }, (_, gi) => {
                                    const picked = (bj._guardIds || [])[gi] || "";
                                    return (
                                      <div key={gi} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <span style={{
                                          background: "#0B1C2E", color: "#fff", borderRadius: 5,
                                          padding: "2px 7px", fontSize: 10, fontWeight: 700, flexShrink: 0,
                                        }}>G{gi + 1}</span>
                                        <select
                                          value={picked}
                                          onChange={(e) => {
                                            const next = [...(bj._guardIds || [])];
                                            next[gi] = e.target.value;
                                            update({ _guardIds: next });
                                          }}
                                          style={{ fontSize: 11, flex: 1, minWidth: 0 }}
                                        >
                                          <option value="">Use step 3 / leave for pool…</option>
                                          {guards.map((g) => (
                                            <option key={g.id} value={g.id}>
                                              {g.name}{g.role === "owner" ? " (owner)" : ""}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    );
                                  })}
                                </div>
                                {(bj.stops || []).map((st, si) => (
                                  <div key={si} style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr 72px 24px", gap: 8, alignItems: "center" }}>
                                    <input value={st.locationName || ""}
                                      onChange={(e) => updateStop(si, { locationName: e.target.value })}
                                      placeholder="Pickup name"
                                      style={{ fontSize: 11, fontWeight: 600 }} />
                                    {/* Same verified-✓ treatment as the DAI rows —
                                        green check once a Places pick captured
                                        coords, so the owner sees the address
                                        verify in real time here too. */}
                                    <div style={{ position: "relative" }}>
                                      <PlacesAutocomplete
                                        value={st.address || ""}
                                        onChange={(addr, lat, lng) => updateStop(si, {
                                          address: addr,
                                          ...(lat != null ? { lat, lng } : {}),
                                        })}
                                        placeholder="Address"
                                        style={{ fontSize: 11, width: "100%", paddingRight: st.lat != null ? 22 : undefined }}
                                      />
                                      {st.lat != null && (
                                        <span style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", fontSize: 10, color: "#2d7a2d", pointerEvents: "none" }}>✓</span>
                                      )}
                                    </div>
                                    {/* This stop's own time. Blank inherits the
                                        row's, which is what every stop did
                                        before — fine when one time covers the
                                        run, wrong when the detail block gives
                                        each consignee its own. */}
                                    <input
                                      value={st.scheduledTime || ""}
                                      onChange={(e) => updateStop(si, { scheduledTime: e.target.value })}
                                      placeholder={bj.scheduledTime || "HH:MM"}
                                      title="This stop's time. Blank uses the job's time."
                                      style={{ fontSize: 11, textAlign: "center" }}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => removeStop(si)}
                                      disabled={(bj.stops || []).length <= 1}
                                      title={(bj.stops || []).length <= 1
                                        ? "A job needs at least one stop"
                                        : "Remove this stop from this job"}
                                      style={{
                                        background: "none",
                                        border: "1px solid " + ((bj.stops || []).length <= 1 ? "#eee" : "#e8a5a5"),
                                        color: (bj.stops || []).length <= 1 ? "#ccc" : "#a32d2d",
                                        borderRadius: 5, padding: "2px 0", fontSize: 11, lineHeight: 1,
                                        cursor: (bj.stops || []).length <= 1 ? "default" : "pointer",
                                      }}
                                    >✕</button>
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  onClick={() => setParsedBulkJobs((arr) => arr.map((x, j) => j !== i ? x : ({
                                    ...x,
                                    stops: [...(x.stops || []), { locationName: "", address: "", type: bj.jobType === "inbound" ? "delivery" : "pickup" }],
                                  })))}
                                  style={{ alignSelf: "flex-start", background: "#f5f8ff", border: "1px dashed #c0d4eb", color: "#185FA5", borderRadius: 6, padding: "4px 8px", fontSize: 10, cursor: "pointer" }}
                                >+ Add {bj.jobType === "inbound" ? "delivery" : "pickup"} to this job</button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div style={{
                      padding: "8px 14px", background: "#fafbfc",
                      borderTop: "1px solid #f0f0f0", fontSize: 10, color: "#666",
                    }}>
                      💡 Below: pick the broker (Malca) and confirm direction. Each row above creates an independent job.
                    </div>
                  </div>
                )}

                {/* Multi-detail preview (DAI schedule emails) — one row per detected
                    detail. Owner deselects any rows that shouldn't become jobs and
                    can edit any field inline. On Create we loop through the
                    selected rows, sharing a bundleId. */}
                {parsedDetails.length >= 2 && (
                  <div style={{
                    background: "#fff", border: "1px solid #d6e0ee", borderRadius: 8,
                    padding: 0, overflow: "hidden",
                  }}>
                    <div style={{
                      padding: "10px 14px", background: "#eef3fb", borderBottom: "1px solid #d6e0ee",
                      fontSize: 12, fontWeight: 700, color: "#185FA5",
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                    }}>
                      <span>📋 {parsedDetails.length} details detected — one job will be created per checked row</span>
                      <span style={{ fontSize: 11, fontWeight: 500, color: "#666" }}>
                        {parsedDetails.filter((d) => d._selected).length} selected
                      </span>
                    </div>
                    <div style={{ maxHeight: 480, overflowY: "auto" }}>
                      {parsedDetails.map((d, i) => {
                        // Update this row, AND propagate name/address edits
                        // to any other parsed row that points at the same
                        // saved location id. Trip 1's "To" can be the same
                        // physical location as trip 2's "From" (Belk 01 in
                        // the Iberjoya example), and the owner expects
                        // renaming once to update everywhere.
                        const update = (patch) => setParsedDetails((arr) => {
                          // Figure out which locationIds are affected by
                          // THIS patch on THIS row, so we know which other
                          // rows to mirror to.
                          const fromIdNow = arr[i]?.fromLocationId || null;
                          const toIdNow = arr[i]?.toLocationId || null;
                          const fromMirror = fromIdNow && (
                            "fromName" in patch || "fromAddress" in patch ||
                            "fromLat" in patch || "fromLng" in patch
                          );
                          const toMirror = toIdNow && (
                            "toName" in patch || "toAddress" in patch ||
                            "toLat" in patch || "toLng" in patch
                          );
                          return arr.map((x, j) => {
                            if (j === i) return { ...x, ...patch };
                            const sidePatch = {};
                            // Mirror the FROM-side edit onto any row whose
                            // from OR to references the same locationId.
                            if (fromMirror && x.fromLocationId === fromIdNow) {
                              if ("fromName" in patch) sidePatch.fromName = patch.fromName;
                              if ("fromAddress" in patch) sidePatch.fromAddress = patch.fromAddress;
                              if ("fromLat" in patch) sidePatch.fromLat = patch.fromLat;
                              if ("fromLng" in patch) sidePatch.fromLng = patch.fromLng;
                            }
                            if (fromMirror && x.toLocationId === fromIdNow) {
                              if ("fromName" in patch) sidePatch.toName = patch.fromName;
                              if ("fromAddress" in patch) sidePatch.toAddress = patch.fromAddress;
                              if ("fromLat" in patch) sidePatch.toLat = patch.fromLat;
                              if ("fromLng" in patch) sidePatch.toLng = patch.fromLng;
                            }
                            // Same idea for TO-side edits.
                            if (toMirror && x.fromLocationId === toIdNow) {
                              if ("toName" in patch) sidePatch.fromName = patch.toName;
                              if ("toAddress" in patch) sidePatch.fromAddress = patch.toAddress;
                              if ("toLat" in patch) sidePatch.fromLat = patch.toLat;
                              if ("toLng" in patch) sidePatch.fromLng = patch.toLng;
                            }
                            if (toMirror && x.toLocationId === toIdNow) {
                              if ("toName" in patch) sidePatch.toName = patch.toName;
                              if ("toAddress" in patch) sidePatch.toAddress = patch.toAddress;
                              if ("toLat" in patch) sidePatch.toLat = patch.toLat;
                              if ("toLng" in patch) sidePatch.toLng = patch.toLng;
                            }
                            return Object.keys(sidePatch).length ? { ...x, ...sidePatch } : x;
                          });
                        });
                        const fromHasCoords = d.fromLat != null && d.fromLng != null;
                        const toHasCoords = d.toLat != null && d.toLng != null;
                        const needsAddress = !d.outOfTown && (!d.fromAddress || !d.toAddress);
                        return (
                          <div key={i} style={{
                            borderBottom: "1px solid #f0f0f0",
                            background: d._selected ? "#fff" : "#fafafa",
                            opacity: d._selected ? 1 : 0.55,
                          }}>
                            {/* Top row — checkbox · date · time · from/to summary · OOT · expand */}
                            <div className="jcm-parsed-row" style={{
                              display: "grid",
                              gridTemplateColumns: "24px 95px 70px 1fr 50px 22px",
                              gap: 8, padding: "8px 14px", alignItems: "center", fontSize: 11,
                            }}>
                              <input
                                type="checkbox"
                                checked={!!d._selected}
                                onChange={(e) => update({ _selected: e.target.checked })}
                              />
                              <input type="date" value={d.scheduledDate || ""}
                                onChange={(e) => update({ scheduledDate: e.target.value })}
                                style={{ fontSize: 11 }} />
                              <Time24Field value={d.scheduledTime || ""}
                                onChange={(v) => update({ scheduledTime: v })}
                                style={{ fontSize: 11 }} />
                              <div style={{
                                fontSize: 11, color: "#444",
                                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                              }}>
                                <span style={{ fontWeight: 600 }}>{d.fromName || "From?"}</span>
                                <span style={{ color: "#bbb" }}> → </span>
                                <span style={{ fontWeight: 600 }}>
                                  {d.outOfTown ? <span style={{ color: "#a36b1d" }}>OUT OF TOWN</span> : (d.toName || "To?")}
                                </span>
                                {needsAddress && (
                                  <span style={{ marginLeft: 6, fontSize: 9, color: "#a36b1d", fontWeight: 700 }}>
                                    📍 needs address
                                  </span>
                                )}
                              </div>
                              <label style={{ fontSize: 10, display: "flex", alignItems: "center", gap: 3, color: "#a36b1d" }}>
                                <input type="checkbox" checked={!!d.outOfTown}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    update({
                                      outOfTown: checked,
                                      ...(checked ? { toName: "OUT OF TOWN", toAddress: "", toLat: null, toLng: null } : {}),
                                    });
                                  }} />
                                OOT
                              </label>
                              <button
                                data-role="jcm-expand-toggle"
                                onClick={() => update({ _expanded: !d._expanded })}
                                title={d._expanded ? "Collapse" : "Edit addresses"}
                                style={{
                                  background: "none", border: "1px solid #ddd",
                                  borderRadius: 4, padding: "1px 4px", cursor: "pointer",
                                  fontSize: 11, color: "#555",
                                }}
                              >{d._expanded ? "▴" : "▾"}</button>
                            </div>

                            {/* Expanded — name + address inputs for from / to.
                                On desktop we honor the d._expanded toggle so the
                                list stays compact. On mobile (handled in
                                OwnerLayout.css), the toggle is hidden and the
                                panel is always shown — vertical space is free,
                                and the tiny chevron-at-bottom-of-stacked-row was
                                getting lost. */}
                            <div
                              className={`jcm-parsed-row jcm-parsed-expand${d._expanded ? " open" : ""}`}
                              style={{
                                padding: "8px 14px 12px 46px",
                                background: "#fafbfc", display: "grid",
                                gridTemplateColumns: "1fr 1fr", gap: 12,
                              }}>
                                {/* FROM */}
                                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                  <div style={{ fontSize: 9, fontWeight: 700, color: "#185FA5", textTransform: "uppercase" }}>From</div>
                                  <input value={d.fromName || ""}
                                    onChange={(e) => update({ fromName: e.target.value })}
                                    placeholder="Origin name"
                                    style={{ fontSize: 11, fontWeight: 600 }} />
                                  <div style={{ position: "relative" }}>
                                    <PlacesAutocomplete
                                      value={d.fromAddress || ""}
                                      onChange={(addr, lat, lng) => update({
                                        fromAddress: addr,
                                        ...(lat != null ? { fromLat: lat, fromLng: lng } : {}),
                                      })}
                                      placeholder="Origin address"
                                      style={{ fontSize: 11, width: "100%", paddingRight: fromHasCoords ? 22 : undefined }}
                                    />
                                    {fromHasCoords && (
                                      <span style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", fontSize: 10, color: "#2d7a2d", pointerEvents: "none" }}>✓</span>
                                    )}
                                  </div>
                                </div>
                                {/* TO */}
                                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                  <div style={{ fontSize: 9, fontWeight: 700, color: "#0E7C46", textTransform: "uppercase" }}>To</div>
                                  {d.outOfTown ? (
                                    <div style={{
                                      fontSize: 11, fontStyle: "italic", color: "#a36b1d",
                                      padding: "8px 0",
                                    }}>
                                      OUT OF TOWN — guard follows rep ~10 min then breaks off. No address needed.
                                    </div>
                                  ) : (
                                    <>
                                      <input value={d.toName || ""}
                                        onChange={(e) => update({ toName: e.target.value })}
                                        placeholder="Destination name"
                                        style={{ fontSize: 11, fontWeight: 600 }} />
                                      <div style={{ position: "relative" }}>
                                        <PlacesAutocomplete
                                          value={d.toAddress || ""}
                                          onChange={(addr, lat, lng) => update({
                                            toAddress: addr,
                                            ...(lat != null ? { toLat: lat, toLng: lng } : {}),
                                          })}
                                          placeholder="Destination address"
                                          style={{ fontSize: 11, width: "100%", paddingRight: toHasCoords ? 22 : undefined }}
                                        />
                                        {toHasCoords && (
                                          <span style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", fontSize: 10, color: "#2d7a2d", pointerEvents: "none" }}>✓</span>
                                        )}
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            {/* Per-trip notes — captures ambiguous info the
                                broker mentioned for THIS row only (alt dates,
                                handoff offers, "could be next week" etc.).
                                Parser pre-fills d.notes from the email body;
                                owner can edit / clear before save. Saves
                                onto the job's notes field at create time. */}
                            <div className={`jcm-parsed-notes${d._expanded ? " open" : ""}`} style={{
                              padding: "0 14px 12px 46px", background: "#fafbfc",
                            }}>
                              <div style={{ fontSize: 9, fontWeight: 700, color: "#74540C", textTransform: "uppercase", marginBottom: 3 }}>
                                Notes (this trip)
                              </div>
                              <textarea
                                value={d.notes || ""}
                                onChange={(e) => update({ notes: e.target.value })}
                                placeholder="Alt date offered, handoff option, special instructions — anything specific to this trip."
                                rows={2}
                                style={{
                                  width: "100%", fontSize: 11, padding: "5px 8px",
                                  border: "1px solid #ddd", borderRadius: 6, resize: "vertical",
                                  fontFamily: "inherit",
                                }}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div style={{
                      padding: "8px 14px", background: "#fafbfc",
                      borderTop: "1px solid #f0f0f0", fontSize: 10, color: "#666",
                    }}>
                      💡 Below: pick the broker and rep (shared across all). Stops/packages
                      panels are hidden — each detail row above carries its own from/to.
                    </div>
                  </div>
                )}

                {/* Job number (Malca only) + type.
                    The parser still writes parsed.jobNumber into form.jobNumber
                    regardless of broker — so non-Malca jobs that happen to have
                    a number in the email still get it stored, just not shown. */}
                {(() => {
                  // Hide the top-level Malca job# field in bulk mode — each
                  // row in the bulk preview carries its own jobNumber, so a
                  // single "umbrella" number on the form would be misleading
                  // and isn't required for save.
                  const inBulkMode = parsedBulkJobs.some((b) => b._selected) && parsedBulkJobs.filter((b) => b._selected).length >= 2;
                  const showJobNum = (form.brokerName || "").toLowerCase().includes("malca") && !inBulkMode;
                  return (
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: showJobNum ? "1fr 1fr" : "1fr",
                      gap: 12,
                    }}>
                      {showJobNum && (
                        <FieldCol label="Job number *">
                          <input value={form.jobNumber} onChange={(e) => {
                            set("jobNumber", e.target.value);
                            // Editing the number away from the matched job
                            // invalidates the merge chip — clear it so we
                            // don't offer to merge into the wrong job.
                            if (existingJobMatch && e.target.value.trim() !== (existingJobMatch.jobNumber || "").trim()) {
                              setExistingJobMatch(null);
                            }
                          }}
                            placeholder="e.g. MAKY26-1901" />
                        </FieldCol>
                      )}
                      <FieldCol label="Job type *">
                        <div style={{ display: "flex", gap: 4 }}>
                          {["inbound", "outbound", "roundtrip", "direct"].map((t) => (
                            <button key={t} onClick={() => { set("jobType", t); setDirectionReview(null); }}
                              className={`btn btn-sm ${form.jobType === t ? "btn-blue" : ""}`}
                              style={{ flex: 1 }}>
                              {t === "roundtrip" ? "Round trip" : t.charAt(0).toUpperCase() + t.slice(1)}
                            </button>
                          ))}
                        </div>
                      </FieldCol>
                    </div>
                  );
                })()}

                {/* Broker */}
                <FieldCol label="Broker *">
                  <select value={form.brokerId} onChange={(e) => handleBrokerChange(e.target.value)}>
                    <option value="">Select broker...</option>
                    {brokers.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                  {/* Parser saw a broker name in the email but we don't
                      have a matching rate card for it. Surface it so the
                      owner knows why the broker dropdown didn't pre-fill,
                      and links them straight to setup. */}
                  {!form.brokerId && form.brokerName && (
                    <div style={{ fontSize: 11, color: "#74540C", background: "#FFF7E0", border: "1px solid #F0D88C", padding: "5px 9px", borderRadius: 6, marginTop: 4 }}>
                      ⚠ Email mentions <strong>{form.brokerName}</strong> but there's no rate card for that broker.
                      Pick one above, or <a href="/owner/rates" target="_blank" rel="noopener noreferrer" style={{ color: "#185FA5" }}>add it in Rate cards</a> first.
                    </div>
                  )}
                  {form.dispatcherName && (
                    <div style={{ fontSize: 11, color: "#185FA5", background: "#EEF4FF", padding: "5px 9px", borderRadius: 6, marginTop: 4 }}>
                      Dispatcher: {form.dispatcherName}
                    </div>
                  )}
                </FieldCol>

                {/* Sales rep fields — only relevant for direct jobs (DAI-style
                    where the guard meets the rep). Outbound/inbound runs go
                    through warehouses & airports; no rep to text. */}
                {form.jobType === "direct" && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 160px", gap: 12 }}>
                    <FieldCol label="Sales rep name">
                      <SalesRepAutocomplete
                        value={form.salesRepName}
                        brokerId={form.brokerId}
                        knownReps={knownReps}
                        onPick={(rep) => {
                          // Picking a suggestion swaps the rep wholesale —
                          // name AND phone. If the picked rep has no
                          // phone on file, keep whatever's currently in
                          // the phone field rather than blanking it.
                          setForm((f) => ({
                            ...f,
                            salesRepName: rep.name,
                            salesRepPhone: rep.phone || f.salesRepPhone || "",
                          }));
                        }}
                        onChange={(v) => set("salesRepName", v)}
                      />
                    </FieldCol>
                    <FieldCol label="Sales rep phone">
                      <PhoneInput value={form.salesRepPhone}
                        onChange={(v) => set("salesRepPhone", v)} />
                    </FieldCol>
                  </div>
                )}

                {/* Airport / date / time / # guards.
                    For direct: one job-level scheduled time covers the whole detail.
                    For inbound/outbound: time lives on each stop (different
                    stores get picked up at different times), so the job-level
                    time field is hidden — it'd just confuse what to enter where.
                    In multi-detail mode each row above already has its own
                    date/time, so we skip this whole row. */}
                {parsedDetails.length < 2 && parsedBulkJobs.length < 2 && (() => {
                  const isAirportType = ["inbound","outbound","roundtrip"].includes(form.jobType);
                  const isDirect = form.jobType === "direct";
                  // Build column track based on which fields show.
                  const tracks = [];
                  if (isAirportType) tracks.push("80px"); // airport
                  tracks.push("1fr");                     // date
                  if (isDirect) tracks.push("120px");     // job-level time (direct only)
                  tracks.push("90px");                    // guards
                  return (
                    <div style={{ display: "grid", gridTemplateColumns: tracks.join(" "), gap: 12 }}>
                      {isAirportType && (
                        <FieldCol label="Airport">
                          <input value={form.airportCode}
                            onChange={(e) => set("airportCode", e.target.value.toUpperCase())}
                            placeholder="CLT" maxLength={3} />
                        </FieldCol>
                      )}
                      <FieldCol label="Scheduled date">
                        <input type="date" value={form.scheduledDate} onChange={(e) => set("scheduledDate", e.target.value)} />
                        {scheduledYearLooksWrong(form.scheduledDate) && (
                          <div style={{ fontSize: 10, color: "#a36b1d", fontWeight: 600, marginTop: 2 }}>
                            ⚠ Year {String(form.scheduledDate).slice(0, 4)} — is that right? A wrong year files the job under the wrong billing month.
                          </div>
                        )}
                      </FieldCol>
                      {isDirect && (
                        <FieldCol label="Scheduled time">
                          <Time24Field value={form.scheduledTime} onChange={(v) => set("scheduledTime", v)} />
                        </FieldCol>
                      )}
                      <FieldCol label="# of guards">
                        <select value={numGuards} onChange={(e) => setNumGuards(Number(e.target.value))}>
                          {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                      </FieldCol>
                    </div>
                  );
                })()}

                {/* Billing clock start/end moved to invoicing — no flight math at job creation. */}

                {/* Split job */}
                <div>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}>
                    <input type="checkbox" checked={form.isSplit}
                      onChange={(e) => set("isSplit", e.target.checked)}
                      style={{ width: 14, height: 14, cursor: "pointer" }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#444" }}>This is a split job</span>
                  </label>
                  {form.isSplit && (
                    <div style={{ display: "grid", gridTemplateColumns: "110px 1fr", gap: 12, marginTop: 8, marginLeft: 22 }}>
                      <FieldCol label="Sub-label">
                        <select value={form.subJobLabel} onChange={(e) => set("subJobLabel", e.target.value)}>
                          <option value="">—</option>
                          <option value="A">A</option>
                          <option value="B">B</option>
                        </select>
                      </FieldCol>
                      <FieldCol label="Parent job number">
                        <input value={form.parentJobNumber}
                          onChange={(e) => set("parentJobNumber", e.target.value)}
                          placeholder="e.g. MAKY26-1901" />
                      </FieldCol>
                    </div>
                  )}
                </div>

                {/* Stops (with packages nested inside each).
                    Hidden in multi-detail mode — each detail row above
                    already represents one job's from/to stops. */}
                {parsedDetails.length < 2 && parsedBulkJobs.length < 2 && (
                <Section label="Stops & packages" onAdd={addStop} addLabel="+ Add stop">
                  {form.stops.length === 0 ? (
                    <div style={{ fontSize: 11, color: "#bbb", padding: "6px 0" }}>No stops added yet.</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {form.stops.map((stop, idx) => (
                        <StopRow
                          key={stop.id}
                          stop={stop}
                          idx={idx}
                          total={form.stops.length}
                          locations={locations}
                          jobType={form.jobType}
                          preferredCity={cityForAirport(form.airportCode)}
                          packages={form.packages.filter((p) => p.stopId === stop.id)}
                          hawbNum={pkgNumLabel}
                          onUpdate={(k, v)  => updateStop(stop.id, k, v)}
                          onRemove={()      => removeStop(stop.id)}
                          onMove={(dir)     => moveStop(idx, dir)}
                          onSelectLocation={(loc) => selectLocation(stop.id, loc)}
                          onAddPackage={()  => addPackage(stop.id)}
                          onUpdatePackage={updatePackage}
                          onRemovePackage={removePackage}
                          // Replace a saved location's contacts wholesale.
                          // Used by the inline edit / delete UI on each
                          // contact row. We persist + optimistically update
                          // the in-memory locations cache so the row re-
                          // renders without a refetch.
                          onUpdateLocationContacts={async (locationId, newContacts) => {
                            await updateLocation(locationId, { contacts: newContacts });
                            setLocations((prev) => prev.map((l) =>
                              l.id === locationId ? { ...l, contacts: newContacts } : l
                            ));
                          }}
                          onAddContact={async (locationId, contact) => {
                            // Two cases:
                            //   1. locationId is set → existing location;
                            //      append the contact and update.
                            //   2. locationId is null → brand-new address
                            //      typed on this stop. Create the location
                            //      on the fly using the stop's name/
                            //      address/coords, stamp the new id back
                            //      onto the stop, then attach the contact.
                            if (locationId) {
                              const target = locations.find((l) => l.id === locationId);
                              if (!target) return;
                              const newContacts = [...(target.contacts || []), contact];
                              await updateLocation(locationId, { contacts: newContacts });
                              setLocations((prev) => prev.map((l) =>
                                l.id === locationId ? { ...l, contacts: newContacts } : l
                              ));
                              return;
                            }
                            // No locationId — create the location now.
                            const sourceStop = form.stops.find((s) => s.id === stop.id);
                            if (!sourceStop) return;
                            const payload = {
                              companyId,
                              name: sourceStop.locationName || "Untitled location",
                              address: sourceStop.address || "",
                              lat: sourceStop.lat,
                              lng: sourceStop.lng,
                              type: "Other",
                              contacts: [contact],
                            };
                            const ref = await createLocation(payload);
                            // Stamp the new location's id back onto the
                            // stop so subsequent + Add contact actions
                            // append rather than re-create, AND so when
                            // the job saves, upsertLocationFromStop sees
                            // the existing entry and doesn't double up.
                            updateStop(stop.id, "locationId", ref.id);
                            // Add the freshly-created location to local
                            // state so the contacts panel updates without
                            // a page refresh.
                            setLocations((prev) => [
                              ...prev,
                              { id: ref.id, ...payload, jobCount: 0 },
                            ]);
                          }}
                        />
                      ))}
                    </div>
                  )}
                </Section>
                )}

                {/* Unassigned packages (orphans) — also hidden in multi-detail mode */}
                {parsedDetails.length < 2 && parsedBulkJobs.length < 2 && form.packages.some((p) => !p.stopId || !form.stops.find((s) => s.id === p.stopId)) && (
                  <div style={{
                    background: "#fff8e6", border: "1px solid #f5d88a", borderRadius: 8,
                    padding: "10px 12px", display: "flex", flexDirection: "column", gap: 8,
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#8a6100" }}>
                      ⚠️ Unassigned packages — pick a stop for each
                    </div>
                    {form.packages
                      .filter((p) => !p.stopId || !form.stops.find((s) => s.id === p.stopId))
                      .map((pkg) => (
                        <div key={pkg.id} style={{ display: "grid", gridTemplateColumns: "130px 1fr 90px 1fr 22px", gap: 6, alignItems: "center" }}>
                          <input value={pkg.hawbNumber}    onChange={(e) => updatePackage(pkg.id, "hawbNumber",    e.target.value)} placeholder={pkgNumLabel}  style={{ fontSize: 11 }} />
                          <input value={pkg.description}  onChange={(e) => updatePackage(pkg.id, "description",  e.target.value)} placeholder="Description"   style={{ fontSize: 11 }} />
                          <input type="number" min={0} value={pkg.declaredValue} onChange={(e) => updatePackage(pkg.id, "declaredValue", e.target.value)} placeholder="$ value" style={{ fontSize: 11 }} />
                          <select value={pkg.stopId || ""} onChange={(e) => reassignPackage(pkg.id, e.target.value)} style={{ fontSize: 11 }}>
                            <option value="">— assign to stop —</option>
                            {form.stops.map((s, i) => (
                              <option key={s.id} value={s.id}>Stop {i + 1} — {s.locationName || "(unnamed)"}</option>
                            ))}
                          </select>
                          <button onClick={() => removePackage(pkg.id)} style={{ background: "none", border: "none", color: "#ccc", fontSize: 16, cursor: "pointer", padding: 0, lineHeight: 1 }}>✕</button>
                        </div>
                      ))}
                  </div>
                )}

                {/* Notes — visible to guards in the pool & job detail. */}
                <FieldCol label="Notes (visible to guards)">
                  <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)}
                    placeholder="Any additional notes about this job..." style={{ minHeight: 60 }} />
                </FieldCol>

                {/* Owner-only notes — pricing reminders, broker quirks,
                    rep behavior, anything you don't want the guard to see.
                    The yellow tint makes the visibility difference obvious
                    at a glance even when scanning the form quickly. */}
                <FieldCol label="🔒 Owner notes (private — guards never see this)">
                  <textarea
                    value={form.ownerNotes}
                    onChange={(e) => set("ownerNotes", e.target.value)}
                    placeholder="Pricing notes, billing reminders, things to watch on this job..."
                    style={{
                      minHeight: 60,
                      background: "#fffbe9",
                      border: "1px solid #f0d878",
                    }}
                  />
                </FieldCol>

                {error && <ErrorBar>{error}</ErrorBar>}
              </div>
            )}

            {/* ══ STEP 3: GUARD ASSIGNMENT ══ */}
            {step === 3 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {isBulkSplit ? (
                  <div style={{ background: "#eef3fb", border: "1px solid #d6e0ee", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#185FA5", lineHeight: 1.6 }}>
                    <strong>Split into guard halves.</strong> Guard counts come from the
                    “# guards” picker on the previous screen. Assign each route’s guards below.
                  </div>
                ) : isBulkMulti ? (
                  <div style={{ background: "#eef3fb", border: "1px solid #d6e0ee", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#185FA5", lineHeight: 1.6 }}>
                    <strong>Bulk jobs — {bulkMultiRows.length} independent jobs.</strong> Each one
                    below has its own guard count and its own people, so two jobs running at the
                    same hour can't end up sharing a guard. Leave a slot on “pool” and that
                    position goes out for a guard to grab.
                  </div>
                ) : (
                  <FieldCol label="Number of guards">
                    <div style={{ display: "flex", gap: 6 }}>
                      {[1, 2, 3, 4].map((n) => (
                        <button key={n} onClick={() => setNumGuards(n)}
                          className={`btn btn-sm ${numGuards === n ? "btn-blue" : ""}`}
                          style={{ minWidth: 66 }}>
                          {n} guard{n > 1 ? "s" : ""}
                        </button>
                      ))}
                    </div>
                  </FieldCol>
                )}

                {/* Bulk multi: one block per JOB, not one shared crew. These
                    rows are independent jobs and can run at the same hour in
                    different cities, so a single G1/G2 list sliced from the
                    front put the same person on two of them at once. Each job
                    gets its own count and its own people. Writes the same
                    _guardCount / _guardIds the row editor on step 2 uses. */}
                {isBulkMulti && bulkMultiRows.map((bj) => {
                  const rowIdx = parsedBulkJobs.indexOf(bj);
                  const cnt = bj._guardCount || 1;
                  const patch = (p2) => setParsedBulkJobs((arr) =>
                    arr.map((x, j) => j === rowIdx ? { ...x, ...p2 } : x));
                  const setSlot = (gi, key, val) => {
                    const next = [...(bj[key] || [])];
                    next[gi] = val;
                    patch({ [key]: next });
                  };
                  const stopNames = (bj.stops || [])
                    .map((st) => st.locationName).filter(Boolean).join(" · ");
                  return (
                    <div key={rowIdx} style={{ background: "#f8f9fb", border: "1px solid #e0e6f0", borderRadius: 8, padding: "12px 14px" }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 4 }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#111" }}>
                            {bj.jobNumber || "(no job #)"}
                          </div>
                          <div style={{ fontSize: 10, color: "#888", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {bj.scheduledDate || "no date"}{bj.scheduledTime ? ` · ${bj.scheduledTime}` : ""}
                            {stopNames ? ` · ${stopNames}` : ""}
                          </div>
                        </div>
                        <select
                          value={cnt}
                          onChange={(e) => patch({ _guardCount: Number(e.target.value) })}
                          title="How many guards on this job"
                          style={{ fontSize: 11, padding: "3px 4px", flexShrink: 0 }}
                        >
                          {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n} guard{n > 1 ? "s" : ""}</option>)}
                        </select>
                      </div>
                      {Array.from({ length: cnt }, (_, gi) => (
                        <div key={gi} style={{ display: "grid", gridTemplateColumns: "34px 1fr 120px", gap: 8, alignItems: "center", marginTop: 8 }}>
                          <div style={{
                            background: "#0B1C2E", color: "#fff", borderRadius: 6,
                            padding: "3px 0", fontSize: 11, fontWeight: 700, textAlign: "center",
                          }}>G{gi + 1}</div>
                          <select
                            value={(bj._guardIds || [])[gi] || ""}
                            onChange={(e) => setSlot(gi, "_guardIds", e.target.value)}
                            style={{ fontSize: 12, minWidth: 0 }}
                          >
                            <option value="">Leave for pool…</option>
                            {guards.map((g) => (
                              <option key={g.id} value={g.id}>
                                {g.name}{g.role === "owner" ? " (owner)" : ""}
                              </option>
                            ))}
                          </select>
                          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <span style={{ fontSize: 11, color: "#888" }}>$</span>
                            <input
                              type="number" min={0} step={0.5}
                              placeholder="$/hr"
                              value={(bj._guardRates || [])[gi] || ""}
                              onChange={(e) => setSlot(gi, "_guardRates", e.target.value)}
                              style={{ flex: 1, minWidth: 0, textAlign: "center", fontSize: 12 }}
                            />
                            <span style={{ fontSize: 10, color: "#888" }}>/hr</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}

                {!isBulkMulti && (isBulkSplit
                  ? splitSlots
                  : Array.from({ length: numGuards }, (_, i) => ({ gNum: i + 1, ...GUARD_ROLES[i + 1] }))
                ).map(({ gNum, title, desc }) => {
                  const assignment = form.guardAssignments.find((g) => g.guardNumber === gNum);
                  const ownCar = guardOwnsCar(assignment, gNum);
                  // Split slots keep their route labels; otherwise the label
                  // follows the own-car toggle (no more stale "Passenger").
                  const role = isBulkSplit
                    ? { title, desc }
                    : { title: ownCar ? `G${gNum} — Own car · mileage` : `G${gNum} — Passenger · time only`,
                        desc: ownCar ? "Time + mileage" : "Time only · no mileage" };
                  return (
                    <div key={gNum} style={{ background: "#f8f9fb", border: "1px solid #e0e6f0", borderRadius: 8, padding: "12px 14px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 10 }}>
                        <div style={{
                          background: "#0B1C2E", color: "#fff", borderRadius: 6,
                          padding: "3px 9px", fontSize: 12, fontWeight: 700, flexShrink: 0,
                        }}>G{gNum}</div>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#111" }}>{role.title}</div>
                          <div style={{ fontSize: 10, color: "#888" }}>{role.desc}</div>
                        </div>
                      </div>
                      <div className="jcm-parsed-row" style={{ display: "grid", gridTemplateColumns: "1fr 130px", gap: 10 }}>
                        <select value={assignment?.guardId || ""}
                          onChange={(e) => handleGuardAssign(gNum, e.target.value)}>
                          <option value="">Leave for pool...</option>
                          {guards.map((g) => <option key={g.id} value={g.id}>{g.name}{g.role === "owner" ? " (owner)" : ""}</option>)}
                        </select>
                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                          <span style={{ fontSize: 12, color: "#888", flexShrink: 0 }}>$</span>
                          <input type="number" min={0} step={0.5}
                            placeholder="$/hr"
                            value={assignment?.payRate || ""}
                            onChange={(e) => handleGuardPayRate(gNum, e.target.value)}
                            style={{ flex: 1, textAlign: "center", minWidth: 0 }} />
                          <span style={{ fontSize: 10, color: "#888", flexShrink: 0 }}>/hr</span>
                        </div>
                      </div>
                      {/* Own car vs rides along — only meaningful with 2+ guards.
                          Defaults to the legacy lead/chase rule until changed. */}
                      {numGuards > 1 && assignment?.guardId && !isBulkSplit && (
                        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                          {[{ v: true, label: "🚗 Own car · mileage" }, { v: false, label: "🧍 Rides along · time only" }].map((opt) => {
                            const active = ownCar === opt.v;
                            return (
                              <button
                                key={String(opt.v)}
                                type="button"
                                onClick={() => handleGuardVehicle(gNum, opt.v)}
                                style={{
                                  flex: 1,
                                  border: `1px solid ${active ? "#185FA5" : "#d8e0ec"}`,
                                  background: active ? "#eef3fb" : "#fff",
                                  color: active ? "#185FA5" : "#999",
                                  borderRadius: 7, padding: "6px 8px",
                                  fontSize: 11, fontWeight: 600, cursor: "pointer",
                                }}
                              >
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Summary */}
                {isBulkSplit ? (
                  <div style={{ background: "#f0f5fb", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#333", lineHeight: 1.8 }}>
                    {bulkSplitHalves.map((h) => {
                      const slotNums = h.suffix === "A" ? [1, 2] : [3, 4];
                      const filled = slotNums.slice(0, h.count).filter((n) => form.guardAssignments.find((g) => g.guardNumber === n && g.guardId)).length;
                      return (
                        <div key={h.suffix}>
                          <strong>{h.jobNumber}</strong> · {h.count} guard{h.count > 1 ? "s" : ""}
                          {" · "}{filled} assigned{filled < h.count ? `, ${h.count - filled} to pool` : ""}
                        </div>
                      );
                    })}
                  </div>
                ) : isBulkMulti ? (
                  <div style={{ background: "#f0f5fb", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#333", lineHeight: 1.8 }}>
                    {bulkMultiRows.map((j, i) => {
                      const cnt = j._guardCount || 1;
                      // A row with its own people ignores the shared crew, so
                      // count those instead of the step-3 slots.
                      const filled = (j._guardIds || []).slice(0, cnt).filter(Boolean).length;
                      return (
                        <div key={i}>
                          <strong>{j.jobNumber || `Job ${i + 1}`}</strong> · {cnt} guard{cnt > 1 ? "s" : ""}
                          {" · "}{filled} assigned{filled < cnt ? `, ${cnt - filled} to pool` : ""}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                <div style={{ background: "#f0f5fb", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#333", lineHeight: 1.8 }}>
                  <strong>{numGuards} guard{numGuards > 1 ? "s" : ""}</strong>
                  {" · "}<strong>{assignedGuards.length}</strong> invoice{assignedGuards.length !== 1 ? "s" : ""} will be generated
                  {" · "}<strong>{mileageSets}</strong> mileage set{mileageSets !== 1 ? "s" : ""}
                  {unassigned > 0 && (
                    <div style={{ fontSize: 11, color: "#856404", marginTop: 2 }}>
                      {unassigned} slot{unassigned > 1 ? "s" : ""} unassigned — those positions go to the pool
                    </div>
                  )}
                  {assignedGuards.length === 0 && (
                    <div style={{ fontSize: 11, color: "#856404", marginTop: 2 }}>
                      No guards assigned — job will go to pool for guards to grab
                    </div>
                  )}
                </div>
                )}

                {error && <ErrorBar>{error}</ErrorBar>}
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{
            padding: "12px 20px", borderTop: "1px solid #e8e8e8",
            display: "flex", justifyContent: "space-between", gap: 8, flexShrink: 0,
          }}>
            <div>
              {step > 1 && (
                <button className="btn" onClick={() => { setError(""); setStep(step - 1); }}>
                  ← Back
                </button>
              )}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn" onClick={onClose}>Cancel</button>
              {step < 3 ? (
                <button className="btn btn-blue" onClick={() => { setError(""); setStep(step + 1); }}>
                  Next →
                </button>
              ) : (
                <button className="btn btn-green" onClick={handleSave} disabled={saving}>
                  {saving ? "Creating..." : "✓ Create job"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

    </>
  );
}

// ══════════════════════════════════════════════════════════
// Stop row with location autocomplete
// ══════════════════════════════════════════════════════════
function StopRow({
  stop, idx, total, locations, packages = [], jobType, preferredCity = "",
  onUpdate, onRemove, onMove, onSelectLocation,
  onAddPackage, onUpdatePackage, onRemovePackage,
  onAddContact, onUpdateLocationContacts,
  hawbNum = "HAWB #",    // broker-specific name (Receipt No. / Order No.)
}) {
  // Inline-add-contact panel state. When toggled on, an editable form
  // appears next to the existing contacts list. Save → calls
  // onAddContact, which persists to the saved location and updates the
  // parent's `locations` cache so the new contact appears here too.
  const [addingContact, setAddingContact] = useState(false);
  const [newContact, setNewContact] = useState({ name: "", phone: "", email: "", role: "" });
  const [savingContact, setSavingContact] = useState(false);
  // Inline-edit state for an existing contact row. `key` identifies which
  // row is open (one at a time); `draft` holds the editable fields.
  const [editingContact, setEditingContact] = useState(null);
  // Stop type & per-stop time visibility:
  //   inbound/outbound — type is auto-derived from position (airport / pickup / delivery)
  //                      and scheduled time is per-stop (different stores at different times)
  //   direct           — both are unnecessary: it's just origin → destination, and the
  //                      job-level scheduledTime represents the whole detail
  const isDirect = jobType === "direct";
  // Round trips mix deliveries and pickups on one run, so the owner picks
  // per stop. Every other direction derives the type from position.
  const showTypePicker = jobType === "roundtrip" && stop.type !== "airport";
  const showPerStopTime = !isDirect;
  const typeBadgeColor = {
    pickup:   { bg: "#E6F1FB", color: "#185FA5" },
    delivery: { bg: "#E6F7EC", color: "#2d7a2d" },
    airport:  { bg: "#f0e8ff", color: "#6b2fb3" },
  }[stop.type] || { bg: "#f2f3f5", color: "#555" };
  const [search, setSearch] = useState(stop.locationName || "");
  const [open, setOpen]     = useState(false);
  const containerRef        = useRef();

  // Match by name OR customerName, then rank so locations in the job's
  // airport-city (e.g. Charlotte for a CLT job) surface first.
  const q = search.toLowerCase();
  const city = (preferredCity || "").toLowerCase();
  const matches = search.length >= 1
    ? locations
        .filter((l) =>
          l.name?.toLowerCase().includes(q) ||
          l.customerName?.toLowerCase().includes(q)
        )
        .slice()
        .sort((a, b) => {
          const aLocal = city && a.address?.toLowerCase().includes(city) ? 0 : 1;
          const bLocal = city && b.address?.toLowerCase().includes(city) ? 0 : 1;
          if (aLocal !== bLocal) return aLocal - bLocal;
          // Then most-used first
          return (b.jobCount || 0) - (a.jobCount || 0);
        })
        .slice(0, 12)
    : [];

  // Group matches by customerName
  const groups    = {};
  const ungrouped = [];
  matches.forEach((loc) => {
    if (loc.customerName) {
      if (!groups[loc.customerName]) groups[loc.customerName] = [];
      groups[loc.customerName].push(loc);
    } else {
      ungrouped.push(loc);
    }
  });
  const groupKeys = Object.keys(groups).sort();

  // Google Places fallback: when the typed name doesn't obviously match a
  // saved location, fetch business/address predictions so the owner can
  // pick the real place (and we capture its Place ID). Debounced so we
  // don't fire a request on every keystroke.
  const [googlePreds, setGooglePreds] = useState([]);
  const [googleLoading, setGoogleLoading] = useState(false);
  const exactSavedMatch = matches.some((l) => l.name?.toLowerCase() === q);
  useEffect(() => {
    if (stop.outOfTown || search.trim().length < 3 || exactSavedMatch) {
      setGooglePreds([]);
      return;
    }
    let cancelled = false;
    setGoogleLoading(true);
    const t = setTimeout(async () => {
      const preds = await getPlacePredictions(search);
      if (!cancelled) {
        setGooglePreds(preds.slice(0, 5));
        setGoogleLoading(false);
      }
    }, 280);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search, stop.outOfTown, exactSavedMatch]);

  // Resolve a Google prediction to full details, then fill the stop. No
  // saved locationId (it's new → created on save), but the Place ID rides
  // along so it's dedup-proof.
  async function pickGooglePrediction(pred) {
    setOpen(false);
    const details = await getPlaceDetails(pred.placeId);
    if (!details) return;
    onSelectLocation({
      name: details.name || pred.primary,
      address: details.address,
      lat: details.lat,
      lng: details.lng,
      placeId: details.placeId,
    });
    setSearch(details.name || pred.primary);
  }

  useEffect(() => {
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Keep search text in sync if parent updates (e.g. parsed from email)
  useEffect(() => {
    setSearch(stop.locationName || "");
  }, [stop.locationName]);

  return (
    <div style={{ background: "#f8f9fb", border: "1px solid #e0e6f0", borderRadius: 8, padding: "10px 12px" }}>
      {/* Stop header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: "#888", textTransform: "uppercase" }}>
          Stop {idx + 1}
        </span>
        <div style={{ display: "flex", gap: 3 }}>
          {idx > 0 && (
            <button onClick={() => onMove(-1)}
              style={{ background: "none", border: "1px solid #ddd", borderRadius: 4, fontSize: 11, cursor: "pointer", padding: "1px 6px", color: "#555" }}>
              ↑
            </button>
          )}
          {idx < total - 1 && (
            <button onClick={() => onMove(1)}
              style={{ background: "none", border: "1px solid #ddd", borderRadius: 4, fontSize: 11, cursor: "pointer", padding: "1px 6px", color: "#555" }}>
              ↓
            </button>
          )}
          <button onClick={onRemove}
            style={{ background: "none", border: "none", color: "#ccc", fontSize: 16, cursor: "pointer", padding: "0 3px", lineHeight: 1 }}>
            ✕
          </button>
        </div>
      </div>

      {/* Out-of-town toggle: for direct jobs whose destination is "follow rep
          out of town for 10 min then break" — DAI-style. When checked, the
          location/address fields lock to a sentinel value so we don't try to
          geocode or autocomplete anything. */}
      {jobType === "direct" && (
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#555", marginBottom: 6, cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={!!stop.outOfTown}
            onChange={(e) => {
              const checked = e.target.checked;
              onUpdate("outOfTown", checked);
              if (checked) {
                onUpdate("locationName", "OUT OF TOWN");
                onUpdate("address", "");
                onUpdate("lat", null);
                onUpdate("lng", null);
                setSearch("OUT OF TOWN");
              } else {
                onUpdate("locationName", "");
                setSearch("");
              }
            }}
          />
          Out of town (follow rep ~10 min then break — no fixed destination)
        </label>
      )}

      {/* "Always ask" confirm banner — the parser found a likely (but not
          certain) match to a saved location. The owner links it (reuse,
          no dupe) or keeps it separate. Hidden once linked (locationId) or
          a specific place is chosen (placeId). */}
      {stop.suggestedMatch && !stop.locationId && !stop.placeId && (
        <div style={{
          background: "#fff7e6", border: "1px solid #f0c46a", borderRadius: 7,
          padding: "8px 10px", marginBottom: 6, fontSize: 11, color: "#8a5a00",
          display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
        }}>
          <span style={{ fontSize: 14 }}>⚠️</span>
          <div style={{ flex: 1, minWidth: 170 }}>
            Looks like saved <strong>{stop.suggestedMatch.name}</strong>
            {stop.suggestedMatch.reason ? ` (${stop.suggestedMatch.reason})` : ""}. Same place?
          </div>
          <button
            type="button"
            className="btn btn-sm btn-blue"
            onMouseDown={() => {
              const loc = locations.find((l) => l.id === stop.suggestedMatch.id);
              if (loc) { onSelectLocation(loc); setSearch(loc.name); }
              onUpdate("suggestedMatch", null);
            }}
          >
            Link to it
          </button>
          <button
            type="button"
            className="btn btn-sm"
            onMouseDown={() => onUpdate("suggestedMatch", null)}
          >
            Keep separate
          </button>
        </div>
      )}

      {/* Location name (autocomplete) + type */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 105px", gap: 8, marginBottom: 6 }}>
        <div ref={containerRef} style={{ position: "relative" }}>
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              onUpdate("locationName", e.target.value);
              // A pure rename keeps the link (the saved row updates on save),
              // but once BOTH name and address have moved away from the
              // linked directory entry, the stop no longer refers to it —
              // unlink so the old row's contacts stop rendering here and the
              // save doesn't overwrite that row.
              if (stop.locationId) {
                const linked = locations.find((l) => l.id === stop.locationId);
                const next = { ...stop, locationName: e.target.value };
                if (linked && !stopStillMatchesLocation(next, linked)) {
                  onUpdate("locationId", null);
                }
              }
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder="Location name (search saved)"
            style={{ width: "100%", fontSize: 11, background: stop.outOfTown ? "#f0f0f0" : undefined }}
            disabled={!!stop.outOfTown}
          />
          {open && (matches.length > 0 || googlePreds.length > 0 || googleLoading) && (
            <div style={{
              position: "absolute", top: "100%", left: 0, right: 0, background: "#fff",
              border: "1px solid #ddd", borderRadius: "0 0 7px 7px",
              zIndex: 50, maxHeight: 260, overflowY: "auto",
              boxShadow: "0 6px 16px rgba(0,0,0,0.1)",
            }}>
              {matches.length > 0 && (
                <div style={{
                  padding: "4px 10px 2px", fontSize: 9, fontWeight: 800,
                  color: "#2d7a2d", textTransform: "uppercase", letterSpacing: 0.5,
                  background: "#f3fbf5", borderBottom: "1px solid #e3f2e6",
                }}>
                  ✓ Your saved locations
                </div>
              )}
              {/* Grouped results */}
              {groupKeys.map((customerName) => (
                <React.Fragment key={customerName}>
                  <div style={{
                    padding: "5px 10px 2px", fontSize: 9, fontWeight: 800,
                    color: "#185FA5", textTransform: "uppercase", letterSpacing: 0.5,
                    background: "#f5f8ff", borderBottom: "1px solid #e8eef8",
                  }}>
                    {customerName}
                  </div>
                  {groups[customerName].map((loc) => (
                    <div key={loc.id}
                      onMouseDown={() => { onSelectLocation(loc); setSearch(loc.name); setOpen(false); }}
                      style={{ padding: "6px 10px 6px 18px", cursor: "pointer", fontSize: 11, borderBottom: "1px solid #f0f0f0" }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#f5f8ff"}
                      onMouseLeave={(e) => e.currentTarget.style.background = ""}>
                      <div style={{ fontWeight: 600 }}>{loc.name}</div>
                      {loc.address && <div style={{ fontSize: 10, color: "#888", marginTop: 1 }}>{loc.address}</div>}
                    </div>
                  ))}
                </React.Fragment>
              ))}
              {/* Ungrouped results */}
              {ungrouped.map((loc) => (
                <div key={loc.id}
                  onMouseDown={() => { onSelectLocation(loc); setSearch(loc.name); setOpen(false); }}
                  style={{ padding: "7px 10px", cursor: "pointer", fontSize: 11, borderBottom: "1px solid #f0f0f0" }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "#f5f8ff"}
                  onMouseLeave={(e) => e.currentTarget.style.background = ""}>
                  <div style={{ fontWeight: 600 }}>{loc.name}</div>
                  {loc.address && <div style={{ fontSize: 10, color: "#888", marginTop: 1 }}>{loc.address}</div>}
                </div>
              ))}

              {/* From Google — add a brand-new location (with its Place ID)
                  when it isn't already in your saved list. */}
              {(googlePreds.length > 0 || googleLoading) && (
                <div style={{
                  padding: "4px 10px 2px", fontSize: 9, fontWeight: 800,
                  color: "#185FA5", textTransform: "uppercase", letterSpacing: 0.5,
                  background: "#f5f8ff", borderTop: matches.length > 0 ? "1px solid #e8eef8" : "none",
                  borderBottom: "1px solid #e8eef8",
                }}>
                  {matches.length > 0 ? "Not in your list? From Google" : "From Google"}
                </div>
              )}
              {googleLoading && googlePreds.length === 0 && (
                <div style={{ padding: "7px 10px", fontSize: 11, color: "#999" }}>Searching Google…</div>
              )}
              {googlePreds.map((pred) => (
                <div key={pred.placeId}
                  onMouseDown={() => pickGooglePrediction(pred)}
                  style={{ padding: "7px 10px", cursor: "pointer", fontSize: 11, borderBottom: "1px solid #f0f0f0" }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "#f5f8ff"}
                  onMouseLeave={(e) => e.currentTarget.style.background = ""}>
                  <div style={{ fontWeight: 600 }}>📍 {pred.primary}</div>
                  {pred.secondary && <div style={{ fontSize: 10, color: "#888", marginTop: 1 }}>{pred.secondary}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
        {showTypePicker ? (
          <select value={stop.type} onChange={(e) => onUpdate("type", e.target.value)} style={{ fontSize: 11 }}>
            <option value="pickup">Pickup</option>
            <option value="delivery">Delivery</option>
          </select>
        ) : (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "center",
            background: typeBadgeColor.bg, color: typeBadgeColor.color,
            borderRadius: 6, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4,
            padding: "0 10px", minHeight: 28,
          }}>
            {stop.type === "airport" ? "✈ Airport"
              : isDirect ? (idx === 0 ? "From" : idx === total - 1 ? "To" : "Stop")
              : stop.type === "pickup" ? "Pickup"
              : "Delivery"}
          </div>
        )}
      </div>

      {/* Address (Places autocomplete) + per-stop scheduled time. Direct jobs
          use the job-level scheduledTime instead, so the per-stop time input
          is hidden and the address takes the full row. */}
      <div style={{ display: "grid", gridTemplateColumns: showPerStopTime ? "1fr 105px" : "1fr", gap: 8 }}>
        <div style={{ position: "relative" }}>
          {stop.outOfTown ? (
            <input
              value="(no fixed destination)"
              disabled
              style={{ fontSize: 11, width: "100%", background: "#f0f0f0", color: "#888", fontStyle: "italic" }}
            />
          ) : (
            <PlacesAutocomplete
              value={stop.address}
              onChange={(addr, lat, lng, meta) => {
                onUpdate("address", addr);
                if (lat != null) { onUpdate("lat", lat); onUpdate("lng", lng); }
                // Capture the Google Place ID (canonical dedup key) when
                // the address came from Places. Free-typing clears it so a
                // stale id doesn't cling to a hand-edited address.
                onUpdate("placeId", meta?.placeId || null);
                // Moving the address away from the linked directory entry
                // means this stop no longer refers to that saved location
                // (the parser matched the wrong one and the owner is fixing
                // it). Unlink so the contacts panel stops showing — and
                // mutating — the old location's contacts, and so saving
                // creates/matches the right directory row instead of
                // overwriting the old one.
                if (stop.locationId) {
                  const linked = locations.find((l) => l.id === stop.locationId);
                  const next = { ...stop, address: addr, placeId: meta?.placeId || null };
                  if (linked && !stopStillMatchesLocation(next, linked)) {
                    onUpdate("locationId", null);
                  }
                }
              }}
              placeholder="Address"
              style={{ fontSize: 11, width: "100%", paddingRight: stop.lat ? 22 : undefined }}
            />
          )}
          {stop.lat && (
            <span style={{
              position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
              fontSize: 10, color: "#2d7a2d", pointerEvents: "none",
            }}>✓</span>
          )}
        </div>
        {showPerStopTime && (
          <Time24Field value={stop.scheduledTime}
            onChange={(v) => onUpdate("scheduledTime", v)}
            style={{ fontSize: 11 }} />
        )}
      </div>

      {/* Contacts on file — surfaces saved contacts when this stop's
          location was picked from the directory, OR when a brand-new
          verified address has been typed (in which case clicking +Add
          contact creates the location on the spot and attaches the
          contact). Inline behavior: roster grows naturally as the owner
          builds jobs, with no need to bounce to the Locations page for
          one-off residential deliveries. */}
      {(() => {
        // Skip on out-of-town and airport stops — neither has a saved
        // location to attach contacts to.
        if (stop.outOfTown) return null;
        if (stop.type === "airport") return null;
        // Show when ANY of:
        //   - the stop is linked to a saved location (existing contacts to display);
        //   - the stop has a verified Places address (lat/lng) ready to be saved;
        //   - the parser already attached a contact, even before the owner has
        //     re-confirmed the address (so SHEILA DARJI etc. surface immediately,
        //     not only after a Places autocomplete pick).
        const hasVerifiedAddress = typeof stop.lat === "number" && typeof stop.lng === "number";
        const hasParsedContact = !!(stop.contactName || stop.contactPhone || stop.contactEmail);
        if (!stop.locationId && !hasVerifiedAddress && !hasParsedContact) return null;
        // Bind to the linked saved location only while the stop still
        // recognizably refers to it (place id / name / address). A stale
        // parser link — wrong match, since edited away — must not surface
        // the old row's contacts here: deleting one would remove it from a
        // location this stop no longer points at.
        const linkedLoc = stop.locationId ? locations.find((l) => l.id === stop.locationId) : null;
        const matchedLoc = linkedLoc && stopStillMatchesLocation(stop, linkedLoc) ? linkedLoc : null;
        const savedContacts = matchedLoc && Array.isArray(matchedLoc.contacts)
          ? matchedLoc.contacts.filter((c) => c.name || c.phone || c.email)
          : [];
        // The parser may have pulled a contact off the form (e.g. the
        // Ferrari Pickup block's Contact + Tel rows). Surface it as a
        // preview so the owner can see it'll be added to the location
        // at save time — for BOTH new and matched-existing locations.
        // Skip the preview only if the matched location already has a
        // contact with the same phone (avoid duplicate noise).
        const parsedContactRaw = (stop.contactName || stop.contactPhone || stop.contactEmail)
          ? { name: stop.contactName || "", phone: stop.contactPhone || "", email: stop.contactEmail || "" }
          : null;
        // Dedup mirrors mergeParsedContact in firestore.js — tolerates
        // OCR drift so a slightly-different parse of the same person
        // doesn't surface as a phantom new contact in the preview.
        const alreadyHasParsed = parsedContactRaw
          && isLikelySameContact(parsedContactRaw, savedContacts);
        const parsedContact = (parsedContactRaw && !alreadyHasParsed) ? parsedContactRaw : null;
        const contacts = parsedContact ? [parsedContact, ...savedContacts] : savedContacts;

        async function commitNewContact() {
          // Skip if entirely blank — saves the user from accidentally
          // appending a phantom row when they click Save without typing.
          if (!newContact.name && !newContact.phone && !newContact.email) {
            setAddingContact(false);
            return;
          }
          setSavingContact(true);
          try {
            // Attach via the VERIFIED binding, not the raw locationId — on a
            // stale link (null matchedLoc) this falls into the create-new-
            // location path instead of appending to the wrong saved row.
            await onAddContact(matchedLoc?.id || null, {
              ...newContact,
              id: `c_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
            });
            setNewContact({ name: "", phone: "", email: "", role: "" });
            setAddingContact(false);
          } catch (err) {
            console.error("Couldn't save contact", err);
            alert("Couldn't save contact: " + (err?.message || err));
          }
          setSavingContact(false);
        }

        return (
          <div style={{
            marginTop: 8, padding: "8px 10px",
            background: "#eef4fa", border: "1px solid #c8dcee", borderRadius: 6,
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: "#185FA5", textTransform: "uppercase", letterSpacing: 0.4 }}>
                {matchedLoc
                  ? `📇 Contacts on file (${contacts.length})`
                  : parsedContact
                    ? "📇 Parsed contact (will be saved with location)"
                    : "📇 Save contact for this address"}
              </div>
              {!addingContact && (
                <button
                  type="button"
                  onClick={() => setAddingContact(true)}
                  style={{
                    background: "transparent", border: "1px solid #c8dcee",
                    color: "#185FA5", borderRadius: 5,
                    fontSize: 9, fontWeight: 700, padding: "2px 7px",
                    cursor: "pointer", textTransform: "uppercase", letterSpacing: 0.3,
                  }}
                >
                  + Add contact
                </button>
              )}
            </div>
            {contacts.length === 0 && !addingContact && (
              <div style={{ fontSize: 11, color: "#888", fontStyle: "italic" }}>
                {matchedLoc
                  ? `No contacts saved yet — click "+ Add contact" to create one.`
                  : `Brand-new address. Click "+ Add contact" to add a phone — saves the location for next time.`}
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {contacts.map((c, ci) => {
                // Index 0 is the parser-supplied preview when parsedContact
                // exists; the rest are committed contacts on the location.
                const isParsedRow = parsedContact && ci === 0;
                // Find this contact's index in the saved-location array
                // so update/remove handlers can target it by position.
                const savedIdx = isParsedRow ? -1 : matchedLoc?.contacts?.findIndex((sc, si) =>
                  sc === savedContacts[ci - (parsedContact ? 1 : 0)]
                ) ?? -1;
                const isEditing = editingContact && editingContact.key === `${stop.id}:${ci}`;
                if (isEditing) {
                  return (
                    <div key={ci} style={{
                      padding: 6, background: "#fff",
                      border: "1px solid #c8dcee", borderRadius: 6,
                      display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6,
                    }}>
                      <input
                        value={editingContact.draft.name}
                        onChange={(e) => setEditingContact((s) => ({ ...s, draft: { ...s.draft, name: e.target.value } }))}
                        placeholder="Name"
                        style={{ fontSize: 11, padding: "4px 6px", border: "1px solid #ddd", borderRadius: 5 }}
                      />
                      <input
                        value={editingContact.draft.role || ""}
                        onChange={(e) => setEditingContact((s) => ({ ...s, draft: { ...s.draft, role: e.target.value } }))}
                        placeholder="Role (optional)"
                        style={{ fontSize: 11, padding: "4px 6px", border: "1px solid #ddd", borderRadius: 5 }}
                      />
                      <PhoneInput
                        value={editingContact.draft.phone}
                        onChange={(v) => setEditingContact((s) => ({ ...s, draft: { ...s.draft, phone: v } }))}
                        placeholder="Phone"
                        style={{ fontSize: 11, padding: "4px 6px", border: "1px solid #ddd", borderRadius: 5 }}
                      />
                      <input
                        value={editingContact.draft.email}
                        onChange={(e) => setEditingContact((s) => ({ ...s, draft: { ...s.draft, email: e.target.value } }))}
                        placeholder="Email"
                        style={{ fontSize: 11, padding: "4px 6px", border: "1px solid #ddd", borderRadius: 5 }}
                      />
                      <div style={{ gridColumn: "1 / -1", display: "flex", gap: 6, justifyContent: "flex-end" }}>
                        <button
                          type="button"
                          onClick={() => setEditingContact(null)}
                          style={{
                            background: "transparent", border: "1px solid #ddd",
                            color: "#666", borderRadius: 5,
                            fontSize: 10, fontWeight: 600, padding: "3px 9px", cursor: "pointer",
                          }}
                        >Cancel</button>
                        <button
                          type="button"
                          onClick={async () => {
                            const draft = editingContact.draft;
                            if (isParsedRow) {
                              onUpdate("contactName",  draft.name  || "");
                              onUpdate("contactPhone", draft.phone || "");
                              onUpdate("contactEmail", draft.email || "");
                            } else if (matchedLoc && savedIdx >= 0 && onUpdateLocationContacts) {
                              const newContacts = matchedLoc.contacts.map((sc, si) =>
                                si === savedIdx ? { ...sc, ...draft } : sc
                              );
                              await onUpdateLocationContacts(matchedLoc.id, newContacts);
                            }
                            setEditingContact(null);
                          }}
                          style={{
                            background: "#185FA5", border: "1px solid #185FA5",
                            color: "#fff", borderRadius: 5,
                            fontSize: 10, fontWeight: 700, padding: "3px 9px", cursor: "pointer",
                          }}
                        >Save</button>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={ci} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: "#333" }}>
                    <span style={{ fontWeight: 600 }}>{c.name || "—"}</span>
                    {c.role && (
                      <span style={{ color: "#888", fontSize: 10 }}>· {c.role}</span>
                    )}
                    {c.phone && (
                      <span style={{ color: "#185FA5", fontFamily: "monospace" }}>📞 {formatPhone(c.phone)}</span>
                    )}
                    {c.email && (
                      <span style={{ color: "#185FA5" }}>✉ {c.email}</span>
                    )}
                    <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
                      <button
                        type="button"
                        title="Edit contact"
                        onClick={() => setEditingContact({
                          key: `${stop.id}:${ci}`,
                          draft: { name: c.name || "", phone: c.phone || "", email: c.email || "", role: c.role || "" },
                        })}
                        style={{
                          background: "transparent", border: "none", color: "#185FA5",
                          padding: "2px 4px", cursor: "pointer", fontSize: 11,
                        }}
                      >✏️</button>
                      <button
                        type="button"
                        title="Delete contact"
                        onClick={async () => {
                          if (!window.confirm("Delete this contact?")) return;
                          if (isParsedRow) {
                            onUpdate("contactName",  "");
                            onUpdate("contactPhone", "");
                            onUpdate("contactEmail", "");
                          } else if (matchedLoc && savedIdx >= 0 && onUpdateLocationContacts) {
                            const newContacts = matchedLoc.contacts.filter((_, si) => si !== savedIdx);
                            await onUpdateLocationContacts(matchedLoc.id, newContacts);
                          }
                        }}
                        style={{
                          background: "transparent", border: "none", color: "#a32d2d",
                          padding: "2px 4px", cursor: "pointer", fontSize: 11,
                        }}
                      >🗑</button>
                    </div>
                  </div>
                );
              })}
            </div>
            {addingContact && (
              <div style={{
                marginTop: 6, padding: 8, background: "#fff",
                border: "1px solid #c8dcee", borderRadius: 6,
                display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6,
              }}>
                <input
                  value={newContact.name}
                  onChange={(e) => setNewContact((c) => ({ ...c, name: e.target.value }))}
                  placeholder="Name"
                  style={{ fontSize: 11, padding: "4px 6px", border: "1px solid #ddd", borderRadius: 5 }}
                />
                <input
                  value={newContact.role}
                  onChange={(e) => setNewContact((c) => ({ ...c, role: e.target.value }))}
                  placeholder="Role (optional)"
                  style={{ fontSize: 11, padding: "4px 6px", border: "1px solid #ddd", borderRadius: 5 }}
                />
                <PhoneInput
                  value={newContact.phone}
                  onChange={(v) => setNewContact((c) => ({ ...c, phone: v }))}
                  placeholder="Phone"
                  style={{ fontSize: 11, padding: "4px 6px", border: "1px solid #ddd", borderRadius: 5 }}
                />
                <input
                  type="email"
                  value={newContact.email}
                  onChange={(e) => setNewContact((c) => ({ ...c, email: e.target.value }))}
                  placeholder="Email (optional)"
                  style={{ fontSize: 11, padding: "4px 6px", border: "1px solid #ddd", borderRadius: 5 }}
                />
                <div style={{ gridColumn: "1 / -1", display: "flex", gap: 6, justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    onClick={() => { setAddingContact(false); setNewContact({ name: "", phone: "", email: "", role: "" }); }}
                    disabled={savingContact}
                    style={{ fontSize: 10, padding: "3px 8px", background: "transparent", border: "1px solid #ddd", borderRadius: 5, cursor: "pointer" }}
                  >Cancel</button>
                  <button
                    type="button"
                    onClick={commitNewContact}
                    disabled={savingContact}
                    style={{ fontSize: 10, padding: "3px 10px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 5, cursor: "pointer", fontWeight: 700 }}
                  >{savingContact ? "Saving..." : "Save"}</button>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Packages for this stop (only for pickup / delivery) */}
      {stop.type !== "airport" && (
        <div style={{
          marginTop: 10, paddingTop: 8, borderTop: "1px dashed #d8dfea",
          display: "flex", flexDirection: "column", gap: 5,
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 2 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: "#555", textTransform: "uppercase", letterSpacing: 0.4 }}>
              Packages for this stop
              {packages.length > 0 && (
                <span style={{ color: "#888", fontWeight: 400, marginLeft: 6 }}>
                  ({packages.length})
                </span>
              )}
            </span>
            <button
              onClick={onAddPackage}
              className="btn btn-sm"
              style={{ fontSize: 10, padding: "3px 8px" }}
            >
              + Add package
            </button>
          </div>

          {packages.length === 0 ? (
            <div style={{ fontSize: 11, color: "#bbb", padding: "4px 0" }}>
              No packages yet — add one for each item picked up/delivered here.
            </div>
          ) : (
            <>
              <div style={{
                display: "grid", gridTemplateColumns: "130px 1fr 85px 70px 22px",
                gap: 6, padding: "0 2px", fontSize: 9, fontWeight: 700, color: "#888",
              }}>
                <span>{hawbNum}</span><span>Description</span><span>Declared $</span><span>Weight</span><span></span>
              </div>
              {packages.map((pkg) => (
                <div key={pkg.id} style={{
                  display: "grid", gridTemplateColumns: "130px 1fr 85px 70px 22px",
                  gap: 6, alignItems: "center",
                }}>
                  <input value={pkg.hawbNumber}   onChange={(e) => onUpdatePackage(pkg.id, "hawbNumber",   e.target.value)} placeholder={hawbNum}   style={{ fontSize: 11 }} />
                  <input value={pkg.description} onChange={(e) => onUpdatePackage(pkg.id, "description", e.target.value)} placeholder="Description" style={{ fontSize: 11 }} />
                  <input type="number" min={0} value={pkg.declaredValue} onChange={(e) => onUpdatePackage(pkg.id, "declaredValue", e.target.value)} placeholder="$ value" style={{ fontSize: 11 }} />
                  <input value={pkg.weight}       onChange={(e) => onUpdatePackage(pkg.id, "weight",       e.target.value)} placeholder="e.g. 25lbs" style={{ fontSize: 11 }} />
                  <button onClick={() => onRemovePackage(pkg.id)} style={{ background: "none", border: "none", color: "#ccc", fontSize: 16, cursor: "pointer", padding: 0, lineHeight: 1 }}>✕</button>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// Tiny layout helpers
// ══════════════════════════════════════════════════════════
function FieldCol({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <label style={{ fontSize: 11, fontWeight: 600, color: "#444" }}>{label}</label>
      {children}
    </div>
  );
}

function Section({ label, onAdd, addLabel, children }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "#444", textTransform: "uppercase", letterSpacing: "0.5px" }}>
          {label}
        </span>
        <button className="btn btn-sm" onClick={onAdd}>{addLabel}</button>
      </div>
      {children}
    </div>
  );
}

function ErrorBar({ children }) {
  return (
    <div style={{
      background: "#fff0f0", border: "1px solid #f5c5c5",
      borderRadius: 7, padding: "8px 12px", fontSize: 11, color: "#a32d2d",
    }}>
      {children}
    </div>
  );
}

// SalesRepAutocomplete now lives in src/components/SalesRepAutocomplete.js
// — shared with JobDetailModal so the rep field has the same autocomplete
// behavior whether you're creating or editing.
